/**
 * SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/tenant-admin/TenantUserManagement.tsx
 * PURPOSE: SC-007-USER-LIST — Danh sách + Thêm/Sửa người dùng, Đặt lại mật khẩu,
 *          Vô hiệu/Kích hoạt, Nhập/Xuất — đúng WIREFRAME-V0-MASTER-REGISTRY
 *          L1123-1366 (phạm vi v0 L1127)
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-11 (2026-10-04: nhập tệp khoá theo Mã nhân viên — BS-007-R12 amend)
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + DD-0002 RBAC
 * PILLAR: P1 (Stage 04 - BUILD) + P4 (Quality Gates)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * Issue #869 (bot-059) FIXED (2026-09-15, @coder), root cause #1 only (root
 * cause #2 BE gate investigated + confirmed correct-as-is, không đụng; root
 * cause #3 JWT staleness fixed riêng): handleEditUser() catch-block trước đây
 * luôn làm "soft" (setEditError, modal giữ dirty) bất kể trục lỗi nào — lệch
 * SPEC-0417 §3.5.2 CR-4 (5-state machine pristine→dirty→saving→success→error),
 * vốn yêu cầu org_id-axis failure phải "hard reset to pristine + toast" khác
 * với role/department/position-axis (soft inline retry, giữ nguyên — không
 * đổi). Bản vá đầu (cùng ngày) implement ĐÚNG literal CR-4: org_id-axis
 * failure → re-fetch GET /tenant-users/{id} rồi ghi đè editForm/editUser về
 * dữ liệu server mới nhất + toast.
 *
 * CORRECTION cùng ngày (2026-09-15, session operator ruling — quyết định UX,
 * KHÔNG phải @cto, override CR-4 nguyên văn cho trục org_id — SPEC-0417 CHƯA
 * sửa, reconciliation là việc của người ra quyết định): auto re-fetch +
 * overwrite state khi lỗi là UX xấu — xoá cả field KHÔNG liên quan lỗi (vd
 * full_name) mà TA đang gõ dở. Ruling nguyên văn: "Modal vẫn mở, thông tin
 * vẫn giữ, ép người dùng thao tác 'Hủy' mới là hành vi bình thường nhất". Cả
 * 2 trục lỗi (org_id cascade VÀ role/department/position) giờ HỘI TỤ về cùng
 * hành vi: giữ nguyên editForm/editUser (dirty), modal không đóng, KHÔNG
 * re-fetch — chỉ khác nhau ở thông điệp lỗi hiển thị. Discriminator
 * `'organization_id' in editPayload` (editPayload hoist ra khỏi try) vẫn giữ
 * lại — chỉ dùng để chọn CÂU CHỮ lỗi, không còn chọn state-mutation path.
 * Test: frontend/tests/13-tenant-admin/
 * TenantUserManagement.editOrgAxisErrorPreservesFormState.test.tsx.
 *
 * ISS-ARCH-9 FIXED (2026-08-11, @coder): deriveManagerRole()/suggestDisplayLabel()
 * dùng ngưỡng SAI (level>=4→SHIFT_MANAGER, level===3→BRANCH_MANAGER) lệch khỏi
 * thang canonical backend/app/models/unit_type.py::UNIT_TYPE_ORG_TYPE_MAP
 * (branch=5, work_area=6) — âm thầm hạ role 2 BRANCH_MANAGER thật khi Sửa+Lưu.
 * Đổi sang hằng số module-level BRANCH_LEVEL=5/WORK_AREA_LEVEL=6 dùng chung cho
 * cả 2 hàm + orgCodeNameLabel (gỡ khai báo trùng cục bộ). Xem RELEASE-V0-ISSUES-
 * CONSOLIDATED-2026-08-09.md §49h + test frontend/tests/13-tenant-admin/
 * TenantUserManagement.deriveManagerRole.test.ts.
 *
 * ISS-0017/0018/0020 remediation (2026-07-30, @coder_frontend) theo
 * Báo cáo UI divergence @reviewer 2026-07-30 §3/§4/§5 (bản cục bộ, KHÔNG có trong repo) + BA-DECISIONS-2026-07-30:
 * - ISS-0017: modal Thêm — tiêu đề "Thêm người dùng" + ✕ (D6); "Tên đăng nhập";
 *   dropdown Vai trò = 3 nhãn VI (BA-D-002), suy role backend theo cấp Đơn vị
 *   (BA-D-002 rule 2); GỠ DepartmentPicker (registry L1127 không có "phòng ban");
 *   primary → token coffee-500 (D8); ⓘ tooltip "Nhãn hiển thị" (D10); sửa typo.
 *   ISS-0021 FIXED (2026-07-30): backend TenantUserCreate đã nới validator —
 *   `department` OPTIONAL mọi vai trò (BR-PADM-008 REQ-G.2) → FE gỡ CONTRACT
 *   SHIM, luôn gửi department=null; trường này KHÔNG hiển thị trên UI v0.
 * - ISS-0018: GỠ bulk-select/bulk toolbar/archive/Undo/option "Đã lưu trữ"
 *   (registry L1127); GỠ filter vai trò/trạng thái ngoài spec (toolbar D4 chỉ
 *   [tìm][lọc đơn vị][đếm]); bổ sung [Xuất ▼] (.xlsx/.csv) theo [9]/[9m] (export
 *   client-side từ danh sách đang lọc — backend chưa có endpoint export);
 *   cột Đơn vị hiển thị MÃ (L1319); cột Thao tác → ⁝ menu 3 mục (L1323);
 *   GỠ khối Capability Assignment khỏi Edit modal (L1329).
 * - ISS-0020: mọi nút primary trang này → var(--nqh-coffee-500).
 *
 * @business-context
 * - Domain: Tenant User Administration, Vietnamese F&B
 * - Impact: Operational — admin manages all users in their tenant
 * - Stakeholders: Tenant admins, tenant owners
 *
 * @technical-architecture
 * - Layer: Presentation Layer
 * - Pattern: CRUD table với ⁝ row-menu + modal Thêm/Sửa + Nhập/Xuất
 * - Dependencies: React 18, apiClient, useAuthStore, useOrganizationTree, xlsx
 *
 * @cross-references
 * - Spec SSOT: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md (SC-007 L1123-1366)
 * - Business: docs/01-planning/02-uat-len-lich-cham-cong/BA-DECISIONS-2026-07-30.md (BA-D-001/002/003)
 * - Review: ISS-0017 (§3) · ISS-0018 (§4) · ISS-0020 (§5) — báo cáo UI divergence @reviewer 2026-07-30 (bản cục bộ, KHÔNG có trong repo; quyết định đã fold vào BA-DECISIONS-2026-07-30.md)
 * - Backend API: backend/app/api/v1/tenant_users.py (list, create, update, reset-password)
 *               backend/app/api/v2/tenant_admin/ (status, import preview/commit)
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Navigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { useAuthStore } from "../../store/auth";
import { apiClient, apiClientV2 } from "../../services/api/client";
// DD-0051 v0.3.1 D1 + BR-TENM-013 v0.2.1 REQ-I: Anchor picker (cây tổ chức)
import { AnchorPicker } from "../../components/tenant-admin/AnchorPicker";
import { PortalMenu, type PortalMenuAnchor } from "../../components/common/PortalMenu";
import { PortalTooltip } from "../../components/common/PortalTooltip";
// WP-8b SC-007 GAP-1: cấp đơn vị (org level) cho gợi ý Nhãn hiển thị + suy role Quản lý
import { useOrganizationTree } from "../../hooks/useOrganizationTree";
// ISS-PILOT-32 (2026-08-12): nút Nhập/Xuất — CSS-module + design-token, khớp
// pattern .btn/.btnSecondary của Manager (ManagerAttendancePage.module.css)
// thay cho inline style hex cứng trước đây.
import styles from "./TenantUserManagement.module.css";

// ─────────────────────────────────────────────────────────────────────────────
// BA-D-002 + BA-D-003 (2026-07-30): dropdown Vai trò v0 = đúng 3 nhãn VI
// "Quản trị viên / Quản lý / Nhân viên"; mapping 13 role DD-0002 → 3 nhóm;
// TENANT_OWNER ẨN (tài khoản kỹ thuật, nhóm Quản trị viên); KHÔNG render mã thô.
// ─────────────────────────────────────────────────────────────────────────────
type RoleGroup = "ADMIN" | "MANAGER" | "EMPLOYEE";

// ISS-PILOT-10 REOPEN (2026-08-10, Hang Le): trong nhóm "Quản lý", role thật
// (CORPORATE_MANAGER/BRANCH_MANAGER/SHIFT_MANAGER) được SUY tự động từ cấp
// Đơn vị (deriveManagerRole dưới) — TA không tự chọn được. HEAD_CHEF không
// suy được theo cấp Đơn vị bằng cách nào (không phải khái niệm phân cấp theo
// level) — trước bản vá này modal Thêm/Sửa người dùng KHÔNG có cách nào tạo
// SHIFT_MANAGER/HEAD_CHEF tường minh (chỉ tạo được HEAD_CHEF qua Nhập Excel,
// xem ISS-PILOT-23 §13). Thêm 2 lựa chọn tường minh ghi THẲNG 2 role này,
// không qua derive theo cấp Đơn vị (Đơn vị vẫn bắt buộc chọn như cũ).
type RoleSelection = RoleGroup | "SHIFT_MANAGER" | "HEAD_CHEF";

const ADMIN_ROLE_VALUES = new Set(["TENANT_OWNER", "TENANT_ADMIN"]);
const MANAGER_ROLE_VALUES = new Set([
  "CORPORATE_MANAGER", "BRANCH_MANAGER", "SHIFT_MANAGER", "HEAD_CHEF",
]);

const roleToGroup = (role: string): RoleGroup =>
  ADMIN_ROLE_VALUES.has(role) ? "ADMIN" : MANAGER_ROLE_VALUES.has(role) ? "MANAGER" : "EMPLOYEE";

/** ISS-PILOT-10 REOPEN: ánh xạ role backend THẬT → lựa chọn dropdown — giữ
 * nguyên 2 lựa chọn tường minh (Trưởng ca/Bếp trưởng) khi mở Sửa 1 user đã
 * có role đó, thay vì snap về nhãn nhóm chung "Quản lý" (mất thông tin cụ thể
 * ngay khi mở modal Sửa). */
const roleToSelection = (role: string): RoleSelection =>
  role === "SHIFT_MANAGER" || role === "HEAD_CHEF" ? role : roleToGroup(role);

/** Nhãn nhóm fallback khi user chưa có Nhãn hiển thị (BA-D-003 rule 2 + (d)3) */
const GROUP_LABELS: Record<RoleGroup, string> = {
  ADMIN: "Quản trị viên",
  MANAGER: "Quản lý",
  EMPLOYEE: "Nhân viên",
};

// ─────────────────────────────────────────────────────────────────────────────
// BR-EMP-003 (SC-007 amendment 2026-08-24) — Wireframe SSOT: docs/02-design/
// 04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md § SC-007-USER-LIST:
// chip "Hồ sơ NV" [5b] + filter [2b] + R8 text nguồn chính.
// ─────────────────────────────────────────────────────────────────────────────

/** R8-EMP-MISSING (NGUỒN CHÍNH — định nghĩa DUY NHẤT tại đây): text lỗi chẩn đoán
 * khi tài khoản thiếu hồ sơ nhân viên liên kết (BR-EMP-003-R8, Pattern #15 — cấm
 * empty giả). Định nghĩa 1 lần tại đây; SC-017/SC-019/SC-020 import từ đây khi
 * các màn AS-BUILT đó qua review chính thức (không "chốt" lên màn AS-BUILT). */
export const R8_EMP_MISSING_MESSAGE =
  "Tài khoản của bạn chưa có hồ sơ nhân viên liên kết. Vui lòng liên hệ Quản trị viên của tổ chức để hoàn tất hồ sơ trước khi chấm công.";

export type EmployeeProfileChip = "complete" | "missing" | "not_required";

/** [5b] BR-EMP-003 §2.2: chip trạng thái pairing — "— Không cần" cho role
 * account-only (TENANT_OWNER/TENANT_ADMIN — ngoại lệ R2 có chủ đích); còn lại
 * ✓ Đủ / ! Thiếu theo cờ has_employee_profile từ GET /tenant-users/list.
 * Read-only: màn này KHÔNG có thao tác link/unlink (R6 — không thuộc v0). */
export const employeeProfileChip = (u: {
  role: string;
  has_employee_profile: boolean | null | undefined;
}): EmployeeProfileChip => {
  if (ADMIN_ROLE_VALUES.has(u.role)) return "not_required";
  return u.has_employee_profile ? "complete" : "missing";
};

/** BR-EMP-003 VALIDATE-[10d]: Số CCCD phải đúng 12 chữ số (validator BR-EMP-001) */
const CITIZEN_ID_PATTERN = /^\d{12}$/;

// ISS-ARCH-9 (2026-08-11, @coder fix): 2 hằng số dưới đây PHẢI khớp
// `backend/app/models/unit_type.py::UNIT_TYPE_ORG_TYPE_MAP` ("BRANCH" → 5,
// "WORKAREA" → 6) — đây là 2 nơi khác ngôn ngữ (Python backend / TS frontend)
// không thể auto-import lẫn nhau, nên PHẢI đồng bộ TAY khi thang cấp đổi.
// Trước bản vá này deriveManagerRole dùng ngưỡng SAI (level>=4 → SHIFT_MANAGER,
// level===3 → BRANCH_MANAGER) — lệch so với thang canonical thật (branch=5,
// work_area=6), khiến 2 BRANCH_MANAGER thật (org level=5) khi Sửa+Lưu bị âm
// thầm hạ xuống SHIFT_MANAGER vì 5>=4 khớp nhánh sai. Xem
// docs/05-test/13-test-issues/RELEASE-V0-ISSUES-CONSOLIDATED-2026-08-09.md §49h.
const BRANCH_LEVEL = 5;
const WORK_AREA_LEVEL = 6;

/**
 * BA-D-002 rule 2: lựa chọn "Quản lý" → role backend suy theo cấp Đơn vị đã chọn:
 * HOLDING/OPCO (level ≤ 4, mặc định) → CORPORATE_MANAGER · BRANCH (5) →
 * BRANCH_MANAGER · WORKAREA (≥ 6) → SHIFT_MANAGER. BE validate lại cùng quy tắc.
 */
// `export` thêm ở đây CHỈ để test hồi quy ISS-ARCH-9 import trực tiếp được
// (xem frontend/tests/13-tenant-admin/TenantUserManagement.deriveManagerRole.test.ts)
// — không đổi hành vi runtime, component vẫn dùng qua default export như cũ.
export const deriveManagerRole = (orgLevel: number | null | undefined): string => {
  if (orgLevel != null && orgLevel >= WORK_AREA_LEVEL) return "SHIFT_MANAGER";
  if (orgLevel != null && orgLevel === BRANCH_LEVEL) return "BRANCH_MANAGER";
  return "CORPORATE_MANAGER";
};

/** Giá trị ghi xuống backend cho 1 lựa chọn dropdown (BA-D-002 bảng mapping) */
const groupToRole = (group: RoleGroup, orgLevel: number | null | undefined): string =>
  group === "ADMIN" ? "TENANT_ADMIN" : group === "EMPLOYEE" ? "EMPLOYEE" : deriveManagerRole(orgLevel);

/**
 * ISS-PILOT-26 RE-REOPEN (2026-08-11, @coder fix, @ba xác nhận qua đọc lại
 * TOÀN VĂN wireframe gốc): phần lõi PURE của cột "Đơn vị" (orgCodeNameLabel
 * bên dưới) — tách ra module-level để test hồi quy gọi trực tiếp, không cần
 * render component / mock useOrganizationTree.
 *
 * Tiền đề sai (bản fix trước, cùng ngày): "role Quản lý → luôn hiện tên thật;
 * role Nhân viên + level<WORK_AREA_LEVEL → 'Chưa phân khu vực'". @ba đọc lại
 * TOÀN VĂN wireframe gốc SC-007-USER-LIST (docs/02-design/04-ui-ux-design/
 * persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md dòng 1136-1414) và xác nhận
 * quy tắc DUY NHẤT cho cột này là: hiển thị MÃ đơn vị được gán (anchor), có
 * thể là node BẤT KỲ tới Khu vực làm việc — KHÔNG có điều kiện theo role hay
 * level nào cả. Khái niệm "Chưa phân khu vực" không hề tồn tại trong spec gốc
 * của màn này — đó là hằng số `UNASSIGNED_AREA_LABEL`
 * (backend/app/api/v2/endpoints/manager_staff.py:106) chỉ thuộc về 3 màn
 * nghiệp vụ Manager (Phân ca/Chấm công/Đội ngũ), bị mượn nhầm ghép vào
 * SC-007 qua 2 lượt fix trước đó.
 *
 * Fix: bỏ hẳn phân nhánh theo role/level — orgId có giá trị thì LUÔN hiện
 * "<code> • <name>" (hoặc chỉ <code> nếu thiếu tên). Nhánh orgId null/
 * undefined → "—" xử lý ở orgCodeNameLabel (wrapper gọi hàm này) như cũ,
 * không liên quan bug này.
 * (xem frontend/tests/13-tenant-admin/TenantUserManagement.orgCodeNameLabel.test.ts)
 *
 * ISS-PILOT-31 (2026-08-12, PO chốt): đổi dấu phân cách "-" → "•" (U+2022)
 * theo UI-UX-DECISION-REGISTER.md luật chuẩn <Mã> • <Tên> — CHỈ đổi ký tự
 * phân cách, KHÔNG đụng logic phân nhánh (đã bỏ ở ISS-PILOT-26).
 */
export const computeOrgUnitLabel = (
  code: string | null | undefined,
  name: string | null | undefined
): string => {
  if (!code) return "—";
  return name ? `${code} • ${name}` : code;
};

// BOT-027 (#758) twin bug — cùng pattern lỗi phát hiện ở màn Cơ cấu tổ chức
// (OrganizationManagement/index.tsx formatImportCommitToast): toast cộng gộp
// imported+updated thành 1 số rồi hiện "Đã nhập N người dùng", khiến TA hiểu
// nhầm N người đều MỚI trong khi một phần là cập nhật người đã có.
// BOT-028 (#786), 2026-09-12: tham số conflictRows mới — trước đây conflict dual-create
// Employee (409 do trùng employee_code với bản ghi có sẵn) chỉ ghi log backend, TA import
// xong không biết dòng nào bị bỏ qua hồ sơ nhân viên. Nay backend trả rõ số dòng qua
// `employee_profile_conflicts`, ghép thêm vào toast thay vì im lặng.
export const formatUserImportCommitToast = (
  imported: number,
  updated: number,
  conflictRows: number[] = []
): string => {
  const base =
    imported > 0 && updated > 0
      ? `Đã thêm ${imported} người dùng mới, cập nhật ${updated} người dùng`
      : imported > 0
      ? `Đã thêm ${imported} người dùng mới`
      : updated > 0
      ? `Đã cập nhật ${updated} người dùng`
      : "Đã nhập 0 người dùng";
  return conflictRows.length > 0
    ? `${base} — ${conflictRows.length} dòng (${conflictRows.join(", ")}) bị trùng Mã nhân viên, chưa tạo được hồ sơ`
    : base;
};

/** ISS-PILOT-10 REOPEN: giá trị ghi xuống backend cho 1 lựa chọn dropdown —
 * 2 lựa chọn tường minh (SHIFT_MANAGER/HEAD_CHEF) ghi THẲNG, bỏ qua
 * deriveManagerRole (đã xác nhận backend TenantUserCreate.validate_role chấp
 * nhận mọi giá trị TenantUserRole hợp lệ — đây thuần là fix option dropdown
 * FE, không đụng backend). Còn lại giữ nguyên bảng mapping groupToRole cũ. */
const selectionToRole = (selection: RoleSelection, orgLevel: number | null | undefined): string =>
  selection === "SHIFT_MANAGER" || selection === "HEAD_CHEF" ? selection : groupToRole(selection, orgLevel);

// ISS-PILOT-11 (2026-08-09, @ba quyết định): "OM" từng hardcode thẳng — không đúng
// cho mọi tenant (tenant khác có thể muốn "COO" hay tên khác). Đổi sang đọc từ
// cấu hình tenant (workforce_settings.ui_labels.corporate_manager_label, SSOT
// backend TenantWorkforceConfig.get_corporate_manager_label()) — biến module-level
// này được nạp 1 lần lúc mount (xem effect fetchCorporateManagerLabel bên dưới),
// default "OM" giữ nguyên hành vi cũ cho tenant chưa cấu hình. Chỉ là GỢI Ý điền
// sẵn — TA luôn gõ đè tự do được (🔄⁴ 2026-08-25: đích nay là employees.position,
// vẫn free-text).
let corporateManagerLabelSuggestion = "OM";

/**
 * 🔄⁴ Retrofit (2026-08-25): đổi tên từ suggestDisplayLabel — công thức GIỮ
 * NGUYÊN y hệt, chỉ đổi Ý NGHĨA đích: gợi ý "Chức danh" (employees.position,
 * khối [10d]) thay vì "Nhãn hiển thị" riêng (tenant_users.display_label —
 * field đã BỎ 2026-08-25 — gộp vào Chức danh) theo (Vai trò × cấp Đơn vị):
 * Quản trị viên → "Quản trị viên" · Nhân viên → "Nhân viên" ·
 * Quản lý + cấp trên cơ sở → nhãn cấu hình tenant (mặc định "OM") ·
 * Quản lý + cơ sở/khu vực → "Quản lý chi nhánh".
 */
// ISS-PILOT-23 (2026-08-09, @ba quyết định): nhánh MANAGER trước đây gộp
// orgLevel===3 (BRANCH_MANAGER thật, theo deriveManagerRole ở trên) VÀ
// orgLevel>=4 (SHIFT_MANAGER thật) chung 1 nhãn "Quản lý chi nhánh" — sai cấp
// bậc cho trường hợp level>=4. Khớp ĐÚNG 3 ngưỡng deriveManagerRole dùng để
// ghi role thật xuống backend, không suy nhãn độc lập với logic đó nữa.
// ISS-ARCH-9 (2026-08-11, @coder fix): ngưỡng đổi theo BRANCH_LEVEL/
// WORK_AREA_LEVEL (5/6) cùng lúc với deriveManagerRole ở trên — nếu chỉ sửa
// deriveManagerRole mà bỏ quên hàm này thì gợi ý Chức danh sẽ lệch khỏi
// role thật vừa ghi xuống (vd orgLevel=5 → role BRANCH_MANAGER nhưng nhãn gợi
// ý vẫn "Trưởng ca" theo ngưỡng cũ) — tái diễn đúng lớp bug ISS-ARCH-9.
const suggestPositionLabel = (selection: RoleSelection, orgLevel: number | null | undefined): string => {
  // ISS-PILOT-10 REOPEN (2026-08-10): 2 lựa chọn tường minh dùng đúng nhãn
  // chuẩn đã có sẵn — cùng nhãn nhánh orgLevel>=WORK_AREA_LEVEL bên dưới, cùng
  // SSOT backend/app/api/v2/endpoints/manager_staff.py:98-99, cùng nhãn đã
  // dùng ở luồng Nhập Excel (ISS-PILOT-23 §13) — tái dùng nguyên văn, không
  // phát minh nhãn mới.
  if (selection === "SHIFT_MANAGER") return "Trưởng ca";
  if (selection === "HEAD_CHEF") return "Bếp trưởng";
  if (selection === "ADMIN") return "Quản trị viên";
  if (selection === "MANAGER") {
    if (orgLevel == null) return "";
    if (orgLevel === BRANCH_LEVEL) return "Quản lý chi nhánh";
    if (orgLevel >= WORK_AREA_LEVEL) return "Trưởng ca"; // deriveManagerRole trả SHIFT_MANAGER
    return corporateManagerLabelSuggestion; // orgLevel <= 4 → deriveManagerRole trả CORPORATE_MANAGER
  }
  return "Nhân viên";
};

// ISS-ARCH (2026-08-11, @coder fix): payload PATCH Sửa hồ sơ user — trước đây
// luôn gửi TOÀN BỘ state form, gây 2 bug oan cho user bình thường:
//  (a) phone_number rỗng gửi "" thay vì null → backend Optional[str]+regex
//      chỉ bỏ qua validate khi giá trị là None, không phải chuỗi rỗng → 422
//      cho MỌI user có SĐT trống dù không đụng field này.
//  (b) organization_id luôn có mặt trong payload dù KHÔNG đổi so với bản gốc
//      → trigger nhầm gate S102L-026 (Phase 1B re-anchor authorization, CÓ
//      CHỦ ĐÍCH — không đụng) → 403 oan cho TA thường (non-TENANT_OWNER) dù
//      chỉ sửa SĐT/tên.
// Tách thành hàm thuần (theo đúng pattern deriveManagerRole ở trên) để unit
// test được mà không cần render toàn bộ component (component fetch users +
// org tree + tenant config lúc mount, mock hết chỉ để test build-payload là
// quá tải cho phạm vi bug này).
export const buildEditUserPayload = (
  editForm: {
    full_name: string;
    phone_number: string;
    email: string;
    role: RoleSelection;
    organization_id: string;
  },
  originalOrganizationId: string | null,
  orgLevel: number | null | undefined
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    full_name: editForm.full_name,
    phone_number: editForm.phone_number.trim() || null,
    email: editForm.email.trim(),
    // BA-D-002 rule 3 (+ ISS-PILOT-10 REOPEN: 2 lựa chọn tường minh ghi thẳng)
    role: selectionToRole(editForm.role, orgLevel),
    // BR-EMP-003 (2026-08-24): employee_code KHÔNG gửi qua PATCH user nữa —
    // quản lý thống nhất qua khối [10d] (GET/PATCH|POST employee-profile) để
    // tenant_users.employee_code và employees.employee_code không lệch nhau.
    // 🔄⁴ (2026-08-25): display_label đã BỎ (gộp vào employees.position, PATCH
    // qua khối [10d] employee-profile riêng — xem validateAndBuildProfilePayload).
  };
  // BA-D-001: backfill đơn vị thủ công qua Sửa người dùng — CHỈ gửi
  // organization_id khi giá trị thực sự khác giá trị gốc; nếu không đổi thì
  // OMIT hẳn key (không gửi null, không gửi giá trị cũ) để không trigger
  // nhầm gate S102L-026.
  const newOrgId = editForm.organization_id || null;
  const originalOrgId = originalOrganizationId || null;
  if (newOrgId !== originalOrgId) {
    payload.organization_id = newOrgId;
  }
  return payload;
};

/**
 * 🆕 (2026-08-25, coordinator fix — checkbox "Bổ sung hồ sơ nhân viên ngay",
 * SAU đó @cpo ratify + đính chính BS-007-R26 khớp shipped 2026-08-26, xem
 * khai báo wantsPairing + báo cáo cuối): quyết định khối [10d] có được validate/gửi
 * trong handleEditUser() hay không. Tách thành hàm thuần (theo đúng pattern
 * buildEditUserPayload ở trên) để unit test được bug thật đã fix — trước bản
 * vá này, validateAndBuildProfilePayload() chạy VÔ ĐIỀU KIỆN cho mọi role đi
 * làm, khiến TA chỉ sửa SĐT cho 1 user "! Thiếu hồ sơ" bị chặn Lưu cả form
 * nếu không điền đủ employee_code/hire_date.
 *
 * true khi: (a) user ĐÃ ghép hồ sơ (hasProfile true — khối LUÔN hiện + LUÔN
 * validate, đúng BS-007-R26 không đổi cho case này) HOẶC (b) CHƯA ghép
 * (hasProfile false) NHƯNG TA đã tick checkbox (wantsPairing true).
 * false khi: role Admin (không có khối [10d] — R22), HOẶC chưa ghép + chưa
 * tick checkbox (mặc định) → Lưu field khác không bị chặn bởi Mã NV/Ngày vào
 * làm còn trống.
 */
export const shouldValidateEmployeeProfile = (
  role: RoleSelection,
  hasProfile: boolean | null,
  wantsPairing: boolean
): boolean => role !== "ADMIN" && (hasProfile === true || wantsPairing);

// ISS-0020: primary theo token brand (D8) — không dùng indigo #4f46e5
const PRIMARY = "var(--nqh-coffee-500, #d97706)";
const PRIMARY_SOFT_BG = "var(--nqh-coffee-50, #fdf6ef)";

interface TenantUser {
  id: string;
  username: string;
  email: string | null;
  phone_number: string;
  full_name: string;
  full_name_vietnamese: string | null;
  employee_code: string | null;
  role: string;
  branch_id: string | null;
  organization_id: string | null;      // ISSUE-021: anchor observability
  organization_path: string | null;    // ISSUE-021: subtree display
  // BR-EMP-003 (SC-007 amendment) [5b]: cờ pairing từ LEFT JOIN employees của
  // GET /tenant-users/list — null khi endpoint chưa attach (data cũ/backend cũ)
  has_employee_profile: boolean | null;
  // 🔄⁴ Retrofit (2026-08-25): employees.position khi user đã ghép hồ sơ —
  // thay display_label cũ (BỎ, gộp vào field này). null khi chưa ghép/Admin —
  // roleColumnLabel() fallback về nhãn nhóm.
  employee_position: string | null;
  department: string | null;
  position: string | null;
  is_active: boolean;
  is_verified: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface CreateUserForm {
  password: string;
  full_name: string;
  phone_number: string;
  email: string;
  role: RoleSelection | "";
  // DD-0051 v0.3.1 D1: anchor bắt buộc với mọi vai trò khác Quản trị viên (BS-007-R03)
  organization_id: string;
  // ISS-PILOT-12 (2026-08-09, @ba quyết định): mã nghiệp vụ (vd "NQDL00008") — TA
  // được phép để trống lúc tạo (fill sau qua Sửa); unique theo tenant (DB
  // constraint uq_tenant_employee_code có sẵn, backend trả 409 khi trùng).
  employee_code: string;
  // BR-EMP-003 (SC-007 amendment) khối [10d] "Hồ sơ nhân viên" — chỉ nhập khi
  // Vai trò thuộc nhóm đi làm (R2); ẩn với Quản trị viên. employee_code dùng
  // CHUNG cho tenant_users.employee_code + employees.employee_code (R3/R4).
  hire_date: string;      // Ngày vào làm* — YYYY-MM-DD, mặc định hôm nay
  position: string;       // Chức danh (optional)
  contract_type: string;  // Loại hợp đồng (optional — mặc định full_time)
  citizen_id: string;     // Số CCCD (optional, PII — 12 chữ số, R5)
}

const INITIAL_FORM: CreateUserForm = {
  password: "",
  full_name: "",
  phone_number: "",
  email: "",
  role: "",  // placeholder "Chọn vai trò" (registry L1208)
  organization_id: "",
  employee_code: "",
  // BR-EMP-003 [10d]: hire_date set "hôm nay" lúc MỞ modal (openCreateModal),
  // không hardcode ở đây để không bị đông cứng theo ngày nạp module.
  hire_date: "",
  position: "",
  contract_type: "full_time",
  citizen_id: "",
};

// BR-EMP-003 AC-7 (SC-007 amendment): form khối [10d] dùng CHUNG cho modal Thêm
// (inline trong createForm) và late-pairing qua ⁝ "Bổ sung hồ sơ NV" (state
// pairForm riêng khi mở modal Sửa ở chế độ pairing).
interface PairingForm {
  employee_code: string;
  hire_date: string;      // YYYY-MM-DD — mặc định hôm nay khi mở pairing
  position: string;
  contract_type: string;  // mặc định full_time (Full-time)
  citizen_id: string;
}

const INITIAL_PAIR_FORM: PairingForm = {
  employee_code: "",
  hire_date: "",
  position: "",
  contract_type: "full_time",
  citizen_id: "",
};

/** Ngày hiện tại dạng YYYY-MM-DD (mặc định cho Ngày vào làm — wireframe [10d]) */
const todayISO = (): string => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// WP-8b SC-007 GAP-3: response của POST /api/v2/tenant-admin/users/import/preview
// Track-1b (2026-08-05): khoá đối chiếu = Email (BS-007-R12) + cờ warning trùng ngườu
interface UserImportPreviewRow {
  row: number;
  full_name: string;
  phone: string;      // ISS-PILOT-29: BE nay trả về SĐT trong preview (trước đây thiếu)
  email: string;
  role_code: string;
  unit_code: string;
  // 🆕 (2026-08-25, @tester verify screenshot): BS-007-R11/R23/R24(b) — 2 cột
  // dual-create AND-gate. hire_date là chuỗi GỐC trong tệp (chưa parse) để TA
  // đối chiếu đúng những gì mình gõ.
  employee_code: string;
  hire_date: string;
  label: string;
  ok: boolean;
  action: "add" | "update" | "review" | "skip" | "error"; // skip: khớp Mã NV, không đổi gì (R12 amend 2026-10-04); review: đổi vai trò quản trị (#1270)
  role_change?: string; // "CŨ → MỚI" khi dòng cập nhật đổi vai trò
  username?: string;    // tên đăng nhập đã có (dòng cập nhật)
  reason: string;
  warning?: string;
}

interface UserImportPreview {
  total: number;
  valid: number;
  errors: number;
  error_rows: number[];
  rows: UserImportPreviewRow[];
}

/**
 * Extract a safe string from FastAPI error responses.
 * FastAPI validation returns detail as array [{type, loc, msg, input}] — not a string.
 * Rendering a non-string directly in JSX causes React error #31.
 * Per SPEC-0417 §3: employee creation form must handle error response as string, not object.
 */
const extractErrorMessage = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg ?? String(d)).join(", ");
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return err?.message ?? fallback;
};

/** D10: chú thích qua icon ⓘ + tooltip microcopy.
 * 2026-08-25: đổi từ span[title] (tooltip OS mặc định, không đồng bộ hình thức
 * với manager) sang PortalTooltip + SVG — đúng pattern đã dùng ở
 * ManagerStaffPage/ManagerAttendancePage/ManagerSchedulePage.tsx (ISS-MGR-3).
 * Text truyền qua PortalTooltip ở call-site, không còn prop `text` ở đây. */
const InfoIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={14} height={14} aria-hidden="true" style={{ display: "inline-flex", color: "var(--nqh-gray-500)", verticalAlign: "middle", marginLeft: "2px" }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>
);

/**
 * BR-EMP-003 (SC-007 amendment 2026-08-24) khối [10d] "Hồ sơ nhân viên" — DÙNG
 * CHUNG (định nghĩa 1 lần) cho modal Thêm [10] biến thể vai trò đi làm và modal
 * Sửa [11] (2026-08-24 gộp cửa sổ: [10d] luôn hiện với vai trò đi làm — đã ghép
 * thì load giá trị hiện có, chưa ghép thì bổ sung late-pairing AC-7).
 * 5 field: Mã nhân viên (không bắt buộc — bỏ trống tự sinh, @ba quyết định
 * 2026-08-24) · Ngày vào làm* (mặc định hôm nay) · Chức danh · Loại hợp đồng
 * (mặc định Full-time) · Số CCCD (PII — mã hoá qua service layer R5).
 *
 * 🔄⁴ Retrofit (2026-08-25): field "Nhãn hiển
 * thị" riêng (tenant_users.display_label) BỊ GỘP vào Chức danh
 * (employees.position) — cột này NAY LÀ FIELD DUY NHẤT cho nhãn vai trò hiển
 * thị. Khi Vai trò ∈ nhóm Manager (positionSuggestion !== null) VÀ đang trống
 * → prefill gợi ý theo Vai trò × cấp Đơn vị; TA gõ đè tự do; [Dùng gợi ý] khôi
 * phục. Employee (positionSuggestion === null) → không có gợi ý, Chức danh là
 * chức danh thật admin gõ tay (vd "Pha chế").
 */
const EmployeeProfileBlock: React.FC<{
  form: PairingForm;
  onChange: (patch: Partial<PairingForm>) => void;
  /** @ba quyết định 2026-08-25 (v0): Mã NV KHÓA sau khi tạo — modal Sửa khi đã
   * ghép hồ sơ thì field chỉ đọc, không gửi employee_code qua PATCH. */
  lockEmployeeCode?: boolean;
  /** 🔄⁴ Gợi ý Chức danh (Vai trò × cấp Đơn vị) — null khi Vai trò KHÔNG thuộc
   * nhóm Manager (Employee/rỗng) → ẩn ⓘ tooltip + nút [Dùng gợi ý], Chức danh
   * là free-text thuần không gợi ý. */
  positionSuggestion?: string | null;
  /** true → hiện nút [Dùng gợi ý] cạnh Chức danh (giữ đúng hành vi cũ của
   * Nhãn hiển thị: gõ tay → touched=true → hiện nút; mở modal Sửa → touched
   * ép true ngay để nút luôn sẵn sàng). */
  positionTouched?: boolean;
  /** gõ tay vào Chức danh → set giá trị + đánh dấu touched (caller quản lý state) */
  onPositionInput?: (value: string) => void;
  /** bấm [Dùng gợi ý] → set Chức danh = positionSuggestion + reset touched */
  onUseSuggestion?: () => void;
}> = ({
  form, onChange, lockEmployeeCode = false,
  positionSuggestion = null, positionTouched = false,
  onPositionInput, onUseSuggestion,
}) => (
  <>
    {/* divider "── Hồ sơ nhân viên ──" theo wireframe */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', fontSize: '13px', fontWeight: 500 }}>
      <span style={{ flex: 1, borderTop: '1px solid #e5e7eb' }} aria-hidden="true" />
      Hồ sơ nhân viên
      <span style={{ flex: 1, borderTop: '1px solid #e5e7eb' }} aria-hidden="true" />
    </div>
    <div>
      <label style={labelStyle}>Mã nhân viên *</label>
      <input
        value={form.employee_code}
        // BOT-028 (#786), 2026-09-12: uppercase khi nhập — khớp chuẩn hoá tầng BE
        // (_truncate_employee_code), "NV07"/"nv07" phải hiện cùng 1 dạng ngay khi gõ,
        // không đợi tới lúc Lưu mới thấy khác giá trị đã nhập.
        onChange={(e) => onChange({ employee_code: e.target.value.toUpperCase() })}
        style={{ ...inputStyle, ...(lockEmployeeCode ? { background: '#f3f4f6', color: '#6b7280' } : null) }}
        maxLength={50}
        disabled={lockEmployeeCode}
      />
      {/* 🔄⁶ BS-007-R23 (2026-08-25, user chốt): Mã NV BẮT BUỘC ở mọi kênh —
          đảo lại quyết định auto-gen 2026-08-24. Đã tạo hồ sơ thì Mã NV KHÓA
          không sửa (bất biến sau khi tạo — không đổi). */}
      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
        {lockEmployeeCode
          ? "Không thể sửa mã nhân viên sau khi đã tạo"
          // BOT-028 (#786), 2026-09-12: SỬA — trước ghi "phạm vi đơn vị", nhưng ratify
          // lại (git-blame + @ba/@architect) xác nhận phạm vi đúng là TOÀN TỔ CHỨC (tenant),
          // không phải riêng từng đơn vị/chi nhánh.
          : "Duy nhất trong toàn tổ chức, không phân biệt hoa/thường"}
      </p>
    </div>
    <div>
      <label style={labelStyle}>Ngày vào làm *</label>
      <input
        type="date"
        value={form.hire_date}
        onChange={(e) => onChange({ hire_date: e.target.value })}
        style={inputStyle}
      />
    </div>
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
      <div style={{ flex: '1 1 auto' }}>
        <label style={labelStyle}>
          Chức danh
          {positionSuggestion !== null && (
            <PortalTooltip text="Chức danh của người dùng. Với vai trò Quản lý, hệ thống gợi ý sẵn theo vai trò và cấp đơn vị; có thể sửa lại." tabIndex={0} ariaLabel="Chức danh của người dùng. Với vai trò Quản lý, hệ thống gợi ý sẵn theo vai trò và cấp đơn vị; có thể sửa lại.">
              <InfoIcon />
            </PortalTooltip>
          )}
        </label>
        <input
          value={form.position}
          onChange={(e) => {
            if (onPositionInput) onPositionInput(e.target.value);
            else onChange({ position: e.target.value });
          }}
          style={inputStyle}
          placeholder="VD: Pha chế"
          maxLength={100}
        />
      </div>
      {positionSuggestion !== null && positionTouched && onUseSuggestion && (
        <button
          type="button"
          onClick={onUseSuggestion}
          style={{
            padding: '10px 14px',
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            whiteSpace: 'nowrap',
          }}
        >
          Dùng gợi ý
        </button>
      )}
    </div>
    <div>
      <label style={labelStyle}>Loại hợp đồng</label>
      <select
        value={form.contract_type}
        onChange={(e) => onChange({ contract_type: e.target.value })}
        style={inputStyle}
      >
        <option value="full_time">Full-time</option>
        <option value="part_time">Part-time</option>
        <option value="contract">Hợp đồng</option>
        <option value="temporary">Thời vụ</option>
        <option value="internship">Thực tập</option>
      </select>
    </div>
    <div>
      <label style={labelStyle}>Số CCCD</label>
      <input
        value={form.citizen_id}
        onChange={(e) => onChange({ citizen_id: e.target.value })}
        style={inputStyle}
        placeholder="12 chữ số"
        inputMode="numeric"
        maxLength={12}
      />
    </div>
  </>
);

const TenantUserManagement: React.FC = () => {
  const { isAuthenticated, userRole, capabilities } = useAuthStore();

  const [users, setUsers] = useState<TenantUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create user modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>(INITIAL_FORM);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  // BOT-004(a) fix (2026-08-28, issue #667): banner lỗi render đầu modal
  // nhưng modal dài + `overflowY: auto` — khi TA đang cuộn tới nút Lưu ở
  // cuối form, banner nằm ngoài viewport, KHÔNG tự cuộn tới → cảm giác
  // "bấm Lưu không có gì xảy ra" dù lỗi (vd 403 re-anchor) đã set đúng.
  // Chỉ tái hiện ở màn hình thấp (1280×720) — modal cao hơn viewport nên
  // phải cuộn; màn cao (1440×900+) modal vừa khung nên banner luôn sẵn
  // trong tầm nhìn, dễ bỏ sót khi verify. Dùng ref + useEffect (không phải
  // gọi tay ở từng chỗ setCreateError) để bắt CẢ validate phía client (vd
  // "Vui lòng chọn đơn vị tổ chức") LẪN lỗi 403 từ API — tất cả đều đi qua
  // chung 1 setter.
  const createErrorRef = useRef<HTMLDivElement | null>(null);
  // 🔄⁴ Retrofit (2026-08-25, đổi tên từ createLabelTouched): admin đã chỉnh
  // tay ô Chức danh (khối [10d]) → hiện nút [Dùng gợi ý]. Trước là field
  // "Nhãn hiển thị" riêng (WP-8b GAP-1) — nay gộp vào Chức danh.
  const [createPositionTouched, setCreatePositionTouched] = useState(false);

  // Edit user modal
  const [showEdit, setShowEdit] = useState(false);
  const [editUser, setEditUser] = useState<TenantUser | null>(null);
  const [editForm, setEditForm] = useState<{
    full_name: string;
    phone_number: string;
    email: string;
    role: RoleSelection;
    organization_id: string;
  }>({ full_name: "", phone_number: "", email: "", role: "EMPLOYEE", organization_id: "" });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  // BOT-004(a) fix (2026-08-28, issue #667) — cùng lý do createErrorRef ở trên.
  const editErrorRef = useRef<HTMLDivElement | null>(null);

  // BOT-004(a) fix — cuộn banner lỗi vào tầm nhìn mỗi khi có lỗi mới (kể cả lỗi
  // giống hệt lần trước, vd bấm Lưu 2 lần liên tiếp cùng lỗi — không dùng
  // `createError`/`editError` làm dep trực tiếp thì effect không re-run được vì
  // giá trị "không đổi"; nhưng ở đây setter luôn set lại chuỗi mới nên effect
  // vẫn chạy đúng theo React (so sánh giá trị, không phải identity) — KHÔNG
  // cần cờ đếm lần riêng). `block: 'nearest'` tránh giật cả trang khi banner
  // đã trong tầm nhìn.
  useEffect(() => {
    if (createError) createErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [createError]);
  useEffect(() => {
    if (editError) editErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [editError]);
  // 🔄⁴ Retrofit (2026-08-25, đổi tên từ editLabelTouched): tracks pairForm.position
  // touched-state (Chức danh trong khối [10d] modal Sửa) — thay editForm.display_label cũ.
  const [editPositionTouched, setEditPositionTouched] = useState(false);
  // BR-EMP-003 (2026-08-24 gộp cửa sổ): khối [10d] luôn hiện trong modal Sửa
  // với vai trò đi làm — đã ghép hồ sơ thì prefill từ GET employee-profile,
  // chưa ghép thì submit sẽ POST late-pairing thay vì PATCH.
  const [pairForm, setPairForm] = useState<PairingForm>(INITIAL_PAIR_FORM);
  // true khi user đang Sửa ĐÃ có hồ sơ NV (submit → PATCH), false khi chưa ghép
  // (submit → POST late-pairing). null = vai trò account-only / chưa load xong.
  const [editHasProfile, setEditHasProfile] = useState<boolean | null>(null);
  // 🆕 (2026-08-25, coordinator fix — vấn đề đã verify code thật; SAU đó
  // @cpo ratify + đính chính BS-007-R26 2026-08-26 khớp shipped — bản R26 cũ
  // từng nói khối [10d] "LUÔN hiện" không phân biệt paired/unpaired, checkbox
  // này ĐẢO một phần hành vi đó cho case CHƯA ghép hồ sơ, xem báo cáo cuối). Trước bản vá này,
  // handleEditUser() validate + block Lưu CẢ FORM (kể cả chỉ sửa SĐT) cho MỌI
  // user role đi làm đang "! Thiếu hồ sơ", vì validateAndBuildProfilePayload()
  // chạy vô điều kiện. Checkbox mặc định TẮT → [10d] ẩn, KHÔNG validate/block
  // gì — TA sửa field khác lưu bình thường, chip vẫn giữ "! Thiếu hồ sơ". Bật
  // → [10d] hiện, employee_code/hire_date bắt buộc như thiết kế, submit POST
  // late-pairing SAU KHI PATCH TenantUser thành công. CHỈ áp dụng modal Sửa +
  // case chưa ghép — KHÔNG áp modal Thêm (ghép hồ sơ bắt buộc khi tạo mới vai
  // trò đi làm — thêm checkbox ở đó sẽ cho phép tạo tài khoản account-only
  // ngoài ý định, đúng nguy cơ ISS-ARCH-5 tiền lệ) và KHÔNG áp Import (không
  // có UI dòng-theo-dòng).
  const [wantsPairing, setWantsPairing] = useState(false);

  // Toolbar theo registry L1315-1317: [1] tìm theo tên + [2] lọc theo đơn vị + [3] đếm
  const [searchQuery, setSearchQuery] = useState("");
  // ISSUE-021: organization anchor filter (subtree match via DD-0051 D6 Rule B)
  const [orgFilter, setOrgFilter] = useState<string>("");
  // BR-EMP-003 (SC-007 amendment) [2b]: lọc Hồ sơ NV — "" (Tất cả) | linked (Đủ
  // hồ sơ) | missing (Thiếu hồ sơ); gửi query param employee_profile cho backend
  const [profileFilter, setProfileFilter] = useState<string>("");

  // ⁝ row action menu (registry L1323, D5)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  // F-6 fix: anchor rect cho PortalMenu (menu ⁝ không còn bị overflow card cắt)
  const [menuAnchor, setMenuAnchor] = useState<PortalMenuAnchor | null>(null);

  // WP-8b SC-007 GAP-2: Đặt lại mật khẩu [13] → [13b] (mật khẩu tạm hiện MỘT LẦN)
  const [resetTarget, setResetTarget] = useState<{ id: string; name: string } | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetResult, setResetResult] = useState<{ name: string; password: string } | null>(null);
  const [pwdCopied, setPwdCopied] = useState(false);

  // WP-8b SC-007 GAP-3: Nhập người dùng [14] → [15]
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [importPreview, setImportPreview] = useState<UserImportPreview | null>(null);
  const [committing, setCommitting] = useState(false);
  const [confirmReview, setConfirmReview] = useState(false); // #1270 r2: xác nhận dòng "review"
  const [importToast, setImportToast] = useState("");
  const importFileRef = useRef<HTMLInputElement | null>(null);

  // ISS-0018 #2: [9] Xuất ▼ → menu [9m] (.xlsx/.csv) — export theo bộ lọc đang áp
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportToast, setExportToast] = useState("");
  // BR-EMP-003 (SC-007 amendment) STATE SAVED-DUAL: toast "Đã tạo người dùng và
  // hồ sơ nhân viên" khi Thêm thành công vai trò đi làm (R3 dual-create); late-
  // pairing thành công cũng dùng kênh toast này ("Đã bổ sung hồ sơ nhân viên")
  const [savedToast, setSavedToast] = useState("");

  // WP-8b SC-007 GAP-1: cấp đơn vị theo organization_id (gợi ý Nhãn + suy role Quản lý)
  const { flat: orgFlat } = useOrganizationTree();
  const orgLevelById = useMemo(
    () => new Map(orgFlat.map((n) => [n.id, n.level as number])),
    [orgFlat]
  );
  // ISS-0018 #3: cột Đơn vị hiển thị MÃ đơn vị (registry L1319), đồng bộ cây org
  const orgCodeById = useMemo(
    () => new Map(orgFlat.map((n) => [n.id, n.code])),
    [orgFlat]
  );
  // ISS-PILOT-8 (2026-08-09, @ba quyết định): cột tham chiếu (không phải chủ thể chính
  // của màn) phải hiện "<Mã> - <Tên>", không chỉ mã thô — cần thêm map tên bên cạnh
  // orgCodeById đã có.
  const orgNameById = useMemo(
    () => new Map(orgFlat.map((n) => [n.id, n.name])),
    [orgFlat]
  );
  // ISS-PILOT-26 RE-REOPEN (2026-08-11, @coder fix, @ba xác nhận qua đọc lại
  // TOÀN VĂN wireframe gốc SC-007-USER-LIST): mọi phân nhánh theo role/level
  // (kể cả "Chưa phân khu vực") đã bị GỠ BỎ — quy tắc DUY NHẤT của cột "Đơn
  // vị" theo wireframe gốc là hiện mã đơn vị đã gán (anchor), bất kể node đó
  // ở cấp nào trong cây tổ chức. `role` không còn cần cho quyết định này nữa
  // (logic thật nằm ở computeOrgUnitLabel, module-level, xem rationale đầy đủ
  // cạnh groupToRole ở trên) — hàm này chỉ còn việc lookup 2 map (code/name)
  // rồi delegate, giữ nguyên rule "orgId null/undefined → '—'" như cũ.
  const orgCodeNameLabel = (orgId: string | null | undefined): string => {
    if (!orgId) return "—";
    return computeOrgUnitLabel(orgCodeById.get(orgId), orgNameById.get(orgId));
  };

  // 🔄⁴ Retrofit (2026-08-25, đổi nguồn đọc từ display_label → employee_position
  // — đã tính fallback ở backend list_tenant_users(): employee_position chỉ có
  // giá trị khi user ĐÃ ghép hồ sơ, None khi Admin/chưa ghép). Cột Vai trò hiện
  // "<nhãn nhóm> - <employee_position>" khi có giá trị; thiếu → chỉ hiện nhãn
  // nhóm (logic fallback giữ NGUYÊN y hệt display_label cũ, chỉ đổi nguồn field).
  const roleColumnLabel = (u: { role: string; employee_position: string | null }): string => {
    const groupLabel = GROUP_LABELS[roleToGroup(u.role)];
    const label = u.employee_position?.trim();
    return label ? `${groupLabel} - ${label}` : groupLabel;
  };

  // 🔄⁴ Retrofit (2026-08-25): gợi ý Chức danh CHỈ áp dụng cho nhóm Manager
  // (khi Vai trò ∈ nhóm Manager và đang trống);
  // "" (chưa chọn) / "EMPLOYEE" → null (không gợi ý, Chức danh free-text thuần).
  const positionSuggestionFor = (
    selection: RoleSelection | "",
    orgLevel: number | null | undefined
  ): string | null =>
    selection && selection !== "EMPLOYEE" ? suggestPositionLabel(selection as RoleSelection, orgLevel) : null;

  // GAP-1 (retrofit 2026-08-25 — đích nay là Chức danh, không phải Nhãn hiển
  // thị riêng): đổi Vai trò/Đơn vị khi CHƯA chỉnh tay → tự cập nhật gợi ý (create)
  useEffect(() => {
    if (!createPositionTouched) {
      setCreateForm((prev) => ({
        ...prev,
        position: positionSuggestionFor(prev.role, orgLevelById.get(prev.organization_id) ?? null) ?? "",
      }));
    }
  }, [createForm.role, createForm.organization_id, createPositionTouched, orgLevelById]);

  // GAP-1 (retrofit 2026-08-25): tự cập nhật gợi ý Chức danh (pairForm.position)
  // khi đổi Vai trò/Đơn vị trong Sửa (chưa chỉnh tay) — editPositionTouched ép
  // true ngay lúc mở modal (xem openEditModal) nên trong thực tế hiệu lực chủ
  // yếu khi TA đổi Vai trò/Đơn vị NGAY sau khi bấm [Dùng gợi ý] (reset touched).
  useEffect(() => {
    if (showEdit && !editPositionTouched) {
      setPairForm((prev) => ({
        ...prev,
        position: positionSuggestionFor(editForm.role, orgLevelById.get(editForm.organization_id) ?? null) ?? "",
      }));
    }
  }, [editForm.role, editForm.organization_id, editPositionTouched, showEdit, orgLevelById]);

  // #1270 final review: xác nhận dòng "review" chỉ áp cho ĐÚNG bản xem trước đã đọc —
  // bỏ chọn mỗi khi bản xem trước hoặc tệp đổi (Hủy, nhập lỗi, chọn tệp mới, xem trước mới).
  useEffect(() => { setConfirmReview(false); }, [importPreview, importFile]);

  // Toast nhập/xuất/lưu tự ẩn sau 4s
  useEffect(() => {
    if (!importToast && !exportToast && !savedToast) return;
    const timer = setTimeout(() => { setImportToast(""); setExportToast(""); setSavedToast(""); }, 4000);
    return () => clearTimeout(timer);
  }, [importToast, exportToast, savedToast]);

  // ISS-PILOT-11 (2026-08-09): nạp nhãn gợi ý Quản lý Tập đoàn/Chuỗi theo cấu
  // hình tenant (mặc định "OM" nếu tenant chưa cấu hình) — 1 lần lúc mount.
  useEffect(() => {
    apiClientV2
      .get("/api/v2/tenant/config")
      .then((res) => {
        const label = res.data?.workforce_settings?.ui_labels?.corporate_manager_label;
        if (typeof label === "string" && label.trim()) {
          corporateManagerLabelSuggestion = label.trim();
        }
      })
      .catch(() => { /* giữ default "OM" nếu tenant chưa cấu hình/lỗi mạng */ });
  }, []);

  // Đóng ⁝ menu / menu Xuất khi click ra ngoài
  useEffect(() => {
    if (!menuOpenId && !exportMenuOpen) return;
    const close = () => { setMenuOpenId(null); setExportMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpenId, exportMenuOpen]);

  const PAGE_SIZE = 20;

  // All hooks MUST be called before any conditional return (React Rules of Hooks)
  const fetchUsers = useCallback(async () => {
    if (!isAuthenticated || (userRole !== "TENANT_ADMIN" && userRole !== "TENANT_OWNER")) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params: Record<string, any> = { page, page_size: PAGE_SIZE };
      if (searchQuery) params.search = searchQuery;
      if (orgFilter) params.organization_id = orgFilter;
      // BR-EMP-003 [2b]: lọc chip "Hồ sơ NV" — linked|missing ("" = Tất cả)
      if (profileFilter) params.employee_profile = profileFilter;

      const response = await apiClient.get("/tenant-users/list", { params });
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (err: any) {
      setError(extractErrorMessage(err, "Không tải được danh sách"));
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, orgFilter, profileFilter, isAuthenticated, userRole]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Guard: only TENANT_ADMIN / TENANT_OWNER with TENANT_USER_MANAGE capability
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (userRole !== "TENANT_ADMIN" && userRole !== "TENANT_OWNER") {
    return <Navigate to="/dashboard/staff" replace />;
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.role) return; // select required — chặn bởi HTML validation
    setCreateLoading(true);
    setCreateError("");

    // BS-007-R03 + registry L1343: Đơn vị bắt buộc với mọi vai trò KHÁC Quản trị viên
    const isAdmin = createForm.role === "ADMIN";
    if (!isAdmin && !createForm.organization_id) {
      setCreateError("Vui lòng chọn đơn vị tổ chức");
      setCreateLoading(false);
      return;
    }

    // BS-007-R03 + R19(b): Email bắt buộc ở mọi điểm ghi — chặn tại form,
    // KHÔNG gửi email: null (API strip None rồi báo thành công giả)
    if (!createForm.email.trim()) {
      setCreateError("Vui lòng nhập email — email là tên đăng nhập của tài khoản");
      setCreateLoading(false);
      return;
    }

    // BR-EMP-003 VALIDATE-[10d]: vai trò đi làm (role != Quản trị viên) mà khối
    // [10d] thiếu Ngày vào làm HOẶC Mã nhân viên → chặn Lưu; CCCD sai 12 số →
    // chặn. 🔄⁶ BS-007-R23 (2026-08-25, user chốt): Mã nhân viên BẮT BUỘC —
    // đảo lại quyết định auto-gen 2026-08-24 (KHÔNG còn "BE tự sinh").
    const isWorkingRole = createForm.role !== "ADMIN";
    if (isWorkingRole) {
      if (!createForm.employee_code.trim()) {
        setCreateError("Vui lòng nhập mã nhân viên");
        setCreateLoading(false);
        return;
      }
      if (!createForm.hire_date) {
        setCreateError("Vui lòng nhập ngày vào làm");
        setCreateLoading(false);
        return;
      }
      if (createForm.citizen_id.trim() && !CITIZEN_ID_PATTERN.test(createForm.citizen_id.trim())) {
        setCreateError("Số CCCD không hợp lệ");
        setCreateLoading(false);
        return;
      }
    }

    try {
      const selection = createForm.role as RoleSelection;
      const orgLevel = orgLevelById.get(createForm.organization_id) ?? null;
      // Build payload — omit empty strings cho organization_id (backend expects null hoặc UUID)
      // 🔄⁴ (2026-08-25): display_label đã BỎ — Chức danh (createForm.position,
      // trống khi lưu → dùng gợi ý qua auto-suggest effect hoặc [Dùng gợi ý])
      // đi trong employee_profile bên dưới, KHÔNG còn key riêng ở payload gốc.
      const payload: Record<string, unknown> = {
        password: createForm.password,
        full_name: createForm.full_name,
        phone_number: createForm.phone_number,
        email: createForm.email.trim(),
        // BA-D-002 (+ ISS-PILOT-10 REOPEN: 2 lựa chọn tường minh ghi thẳng)
        role: selectionToRole(selection, orgLevel),
        organization_id: createForm.organization_id || null,
        // ISS-0021 (2026-07-30): backend validator đã nới — department OPTIONAL
        // mọi vai trò (BR-PADM-008 REQ-G.2). v0 registry L1127 không có khái
        // niệm phòng ban → luôn gửi null, KHÔNG hiển thị trên UI.
        department: null,
        // ISS-PILOT-12 (2026-08-09): được phép để trống lúc tạo (fill sau qua
        // Sửa) — gửi null thay vì "" để không đụng UNIQUE(tenant_id,
        // employee_code) với các user khác cũng đang để trống.
        // BR-EMP-003: với vai trò đi làm, Mã nhân viên nhập ở khối [10d] — ghi
        // CHUNG cho tenant_users.employee_code (giữ cột "Mã" nhất quán).
        employee_code: createForm.employee_code.trim() || null,
      };
      // BR-EMP-003 R3 [10d]: khối Hồ sơ nhân viên chỉ gửi cho vai trò đi làm —
      // backend dual-create TenantUser+Employee+link trong 1 transaction; gửi cả
      // khi role account-only là thừa (backend tự bỏ qua) nên omit ở đây.
      if (isWorkingRole) {
        payload.employee_profile = {
          employee_code: createForm.employee_code.trim(),
          hire_date: createForm.hire_date,
          position: createForm.position.trim() || null,
          contract_type: createForm.contract_type || "full_time",
          citizen_id: createForm.citizen_id.trim() || null,
        };
      }
      await apiClient.post("/tenant-users/create", payload);
      setShowCreate(false);
      setCreateForm(INITIAL_FORM);
      setCreatePositionTouched(false);
      // STATE SAVED-DUAL (R3) / SAVED — wireframe sc-007-draft
      setSavedToast(isWorkingRole ? "Đã tạo người dùng và hồ sơ nhân viên" : "Đã lưu người dùng");
      fetchUsers();
    } catch (err: any) {
      setCreateError(extractErrorMessage(err, "Tạo người dùng thất bại"));
    } finally {
      setCreateLoading(false);
    }
  };

  const openCreateModal = () => {
    // BR-EMP-003 [10d]: Ngày vào làm mặc định HÔM NAY tại thời điểm mở modal
    setCreateForm({ ...INITIAL_FORM, hire_date: todayISO() });
    setCreatePositionTouched(false);
    setCreateError("");
    setShowCreate(true);
  };

  // RS-2 (2026-07-30): dialog [12] styled thay window.confirm (registry L1247-1254)
  const [deactivateTarget, setDeactivateTarget] = useState<{ id: string; name: string } | null>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  // BS-007-R19(a): dialog xác nhận khi đổi Email (đổi định danh đăng nhập)
  const [showEmailChangeConfirm, setShowEmailChangeConfirm] = useState(false);
  const emailChangeApproved = useRef(false);

  // --- Sprint 76L: Wire to new v2 tenant-admin lifecycle API ---

  const handleDeactivateConfirm = async () => {
    if (!deactivateTarget) return;
    setDeactivateLoading(true);
    try {
      await apiClientV2.patch(`/api/v2/tenant-admin/users/${deactivateTarget.id}/status`, { action: "deactivate" });
      setDeactivateTarget(null);
      fetchUsers();
    } catch (err: any) {
      setError(extractErrorMessage(err, "Vô hiệu hóa thất bại"));
      setDeactivateTarget(null);
    } finally {
      setDeactivateLoading(false);
    }
  };

  // Kích hoạt: xác nhận nhanh (registry L1323 — không cần dialog)
  const handleActivate = async (userId: string) => {
    try {
      await apiClientV2.patch(`/api/v2/tenant-admin/users/${userId}/status`, { action: "activate" });
      fetchUsers();
    } catch (err: any) {
      setError(extractErrorMessage(err, "Kích hoạt thất bại"));
    }
  };

  const openEditModal = (u: TenantUser) => {
    setEditUser(u);
    setEditError("");
    // ISS-PILOT-10 REOPEN: giữ nguyên lựa chọn tường minh Trưởng ca/Bếp trưởng
    // khi user đã có đúng role đó — không còn snap về nhãn nhóm chung "Quản lý"
    // (mất thông tin cụ thể ngay khi mở modal Sửa).
    const selection = roleToSelection(u.role);
    setEditForm({
      full_name: u.full_name,
      phone_number: u.phone_number || "",
      email: u.email || "",
      // BA-D-002 rule 3 + BA-D-003 rule 3: role ẩn → dropdown snap về nhãn nhóm
      // (trừ 2 lựa chọn tường minh ở trên); Lưu sẽ chuẩn hoá role theo BA-D-002
      role: selection,
      organization_id: u.organization_id || "",
    });
    setEditPositionTouched(true); // giữ Chức danh hiện tại → hiện nút [Dùng gợi ý]
    setWantsPairing(false); // reset checkbox "Bổ sung hồ sơ nhân viên ngay" mỗi lần mở modal
    setShowEdit(true);

    // BR-EMP-003 (2026-08-24 gộp cửa sổ): khối [10d] trong modal Sửa — vai trò
    // đi làm thì load hồ sơ hiện có (đã ghép → prefill; chưa ghép → form trống,
    // Ngày vào làm mặc định hôm nay). GET trả 204 khi chưa ghép.
    if (u.role !== "ADMIN" && u.role !== "TENANT_OWNER" && u.role !== "TENANT_ADMIN") {
      // 🔄⁴ (2026-08-25): prefill Chức danh ngay từ u.employee_position (list
      // response) để tránh nháy rỗng trước khi GET employee-profile (async, xem
      // effect bên dưới) trả về — effect ghi đè lại bằng employee.position
      // chính xác khi có (giữ hành vi "hiện nhãn hiện tại ngay" của display_label cũ).
      setPairForm({ ...INITIAL_PAIR_FORM, employee_code: "", hire_date: todayISO(), position: u.employee_position || "" });
      setEditHasProfile(!!u.has_employee_profile);
      void (async () => {
        try {
          const res = await apiClient.get(`/tenant-users/${u.id}/employee-profile`);
          const p = res.data;
          // 204 / body rỗng → chưa ghép hồ sơ
          setEditHasProfile(!!p);
          if (p) {
            setPairForm({
              employee_code: p.employee_code || "",
              hire_date: p.hire_date || todayISO(),
              position: p.position || "",
              contract_type: p.contract_type || "full_time",
              citizen_id: p.citizen_id || "",
            });
          }
        } catch {
          // lỗi mạng — coi như chưa ghép; submit sẽ POST late-pairing
          setEditHasProfile(false);
        }
      })();
    } else {
      setEditHasProfile(null);
    }
  };

  // BR-EMP-003 (2026-08-24 gộp cửa sổ): submit khối [10d] trong modal Sửa —
  // đã ghép hồ sơ → PATCH employee-profile; chưa ghép → POST late-pairing.
  // Ngày vào làm bắt buộc; CCCD sai 12 số chặn tại form (VALIDATE-[10d]).
  // @ba quyết định 2026-08-25 (v0): đã ghép hồ sơ → KHÔNG gửi employee_code
  // (Mã NV khóa sau khi tạo, BE cũng từ chối nếu payload cố đổi) — nên chỉ
  // check bắt buộc employee_code ở nhánh CHƯA ghép (late-pairing).
  // 🔄⁶ BS-007-R23 (2026-08-25, user chốt): Mã NV BẮT BUỘC khi late-pairing —
  // đảo lại quyết định auto-gen 2026-08-24.
  const validateAndBuildProfilePayload = (): Record<string, unknown> | null => {
    if (!editHasProfile && !pairForm.employee_code.trim()) {
      setEditError("Vui lòng nhập mã nhân viên");
      return null;
    }
    if (!pairForm.hire_date) {
      setEditError("Vui lòng nhập ngày vào làm");
      return null;
    }
    if (pairForm.citizen_id.trim() && !CITIZEN_ID_PATTERN.test(pairForm.citizen_id.trim())) {
      setEditError("Số CCCD không hợp lệ");
      return null;
    }
    if (editHasProfile) {
      return {
        hire_date: pairForm.hire_date,
        position: pairForm.position.trim() || null,
        contract_type: pairForm.contract_type || "full_time",
        citizen_id: pairForm.citizen_id.trim() || null,
      };
    }
    return {
      employee_code: pairForm.employee_code.trim() || null,
      hire_date: pairForm.hire_date,
      position: pairForm.position.trim() || null,
      contract_type: pairForm.contract_type || "full_time",
      citizen_id: pairForm.citizen_id.trim() || null,
    };
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser) return;
    setEditLoading(true);
    setEditError("");

    // BA-D-001 rule 3 + BS-007-R03: lần Sửa đầu tiên của user legacy chưa gán
    // đơn vị buộc phải gán (vai trò khác Quản trị viên) — chặn Lưu
    const isAdmin = editForm.role === "ADMIN";
    if (!isAdmin && !editForm.organization_id) {
      setEditError("Vui lòng chọn đơn vị tổ chức");
      setEditLoading(false);
      return;
    }

    // BS-007-R03 + R19(b): Email bắt buộc ở mọi điểm ghi — thao tác xoá trắng
    // bị chặn tại form, KHÔNG được im lặng bỏ qua
    if (!editForm.email.trim()) {
      setEditError("Vui lòng nhập email — email là tên đăng nhập của tài khoản");
      setEditLoading(false);
      return;
    }

    // BS-007-R19(a): Đổi Email = đổi định danh đăng nhập → xác nhận trước khi lưu.
    // BS-007-R20 (BA-D-008): v0 email đã có → readOnly, đường dialog này KHÔNG kích
    // hoạt; backfill (đang trống → điền) không cần dialog. Giữ cho Track-2.
    const emailChanged =
      editForm.email.trim().toLowerCase() !== (editUser.email || "").trim().toLowerCase();
    const hadEmail = (editUser.email || "").trim() !== "";
    if (emailChanged && hadEmail && !emailChangeApproved.current) {
      setEditLoading(false);
      setShowEmailChangeConfirm(true);
      return;
    }
    emailChangeApproved.current = false;

    // BR-EMP-003 (2026-08-24): vai trò đi làm → validate + gửi cả khối [10d].
    // 🆕 (2026-08-25, checkbox "Bổ sung hồ sơ nhân viên ngay" — xem khai báo
    // wantsPairing ở trên): CHỈ validate/gửi khối [10d] khi (a) user ĐÃ ghép
    // hồ sơ (editHasProfile true — luôn hiện + validate, đúng R26 cho case
    // này, không đổi) HOẶC (b) user CHƯA ghép nhưng TA đã tick checkbox. Nếu
    // chưa ghép VÀ chưa tick → bỏ qua khối [10d] hoàn toàn, không validate/
    // block Lưu cho field khác (fix bug thật: trước đây validate chạy vô điều
    // kiện cho mọi role đi làm, chặn Lưu cả form chỉ để sửa SĐT).
    const shouldSubmitProfile = shouldValidateEmployeeProfile(editForm.role, editHasProfile, wantsPairing);
    let profilePayload: Record<string, unknown> | null = null;
    if (shouldSubmitProfile) {
      profilePayload = validateAndBuildProfilePayload();
      if (profilePayload === null) {
        setEditLoading(false);
        return;
      }
    }

    // ⚠️ Gap đã biết (2026-08-25, KHÔNG phải R3 — chỉ xử lý UX, không phải
    // transaction thật): PATCH TenantUser + (PATCH|POST) employee-profile là
    // 2 API call / 2 transaction ĐỘC LẬP (backend tách endpoint có chủ đích —
    // ngoài phạm vi sửa thành 1 transaction thật). Nếu call 2 lỗi giữa
    // chừng, TenantUser ĐÃ lưu — banner lỗi chung chung "Cập nhật thất bại"
    // sẽ khiến TA tưởng CHƯA lưu gì cả. Tách try/catch riêng cho từng call để
    // phân biệt.
    // Issue #869 (bot-059) root cause #1 + SPEC-0417 §3.5.2 CR-4: `editPayload`
    // phải sống ngoài `try` để catch block đọc lại được — CR-4 phân biệt
    // recovery path theo TRỤC lỗi vừa thất bại (org_id cascade vs
    // role/department/position), và trục đó CHỈ xác định được từ payload
    // thực sự vừa gửi, không phải từ response lỗi (backend không luôn trả
    // lại field nào đã đổi).
    let editPayload: Record<string, unknown> | null = null;
    try {
      const orgLevel = orgLevelById.get(editForm.organization_id) ?? null;
      // GAP-1: Nhãn trống khi lưu → dùng gợi ý. ISS-ARCH (2026-08-11): dùng
      // buildEditUserPayload() để chỉ gửi field THỰC SỰ đổi — xem chú thích
      // đầy đủ tại định nghĩa hàm phía trên.
      editPayload = buildEditUserPayload(editForm, editUser.organization_id, orgLevel);
      await apiClient.patch(`/tenant-users/${editUser.id}`, editPayload);
      // GitHub issue #665 (BOT-002) UAT retest 2026-09-12 (@duyen3923): "lưu
      // thành công, đơn vị đã thay đổi, không còn 403 — nhưng không thấy toast
      // thông báo hiện ra (silent success)". Cùng họ Debugging Pattern #14:
      // request đã ghi THẬT, chỉ UI không xác nhận. Gốc: chỉ 2 nhánh
      // `profilePayload` bên dưới (R29) từng gọi `setSavedToast(...)` — khi TA
      // sửa "thông tin cơ bản" (kể cả organization_id) mà KHÔNG kèm bước
      // employee-profile, không nhánh nào set toast, modal đóng im lặng. Set
      // ở đây, CHỈ khi không có `profilePayload` theo sau — nếu có, nhánh
      // profile bên dưới ghi đè bằng thông điệp cụ thể hơn (R29), tránh 2
      // toast chồng cho cùng một lần Lưu.
      if (!profilePayload) {
        setSavedToast("Đã cập nhật người dùng");
      }
    } catch (err: any) {
      // SPEC-0417 §3.5.2 CR-4 nguyên văn: "org_id cascade error = hard error
      // with full-state reset to pristine + toast; department/position/role
      // error = soft inline retry, field retains dirty state". Discriminator
      // đúng-trục: buildEditUserPayload() CHỈ gắn key `organization_id` khi
      // giá trị đó thực sự đổi so với bản gốc (xem chú thích tại định nghĩa
      // hàm) — nên "key có mặt trong payload vừa gửi" phản ánh đúng trục lỗi
      // vừa thất bại, không suy đoán từ response.
      const isOrgAxisFailure = editPayload !== null && "organization_id" in editPayload;

      // Issue #869 (bot-059) correction (2026-09-15, user ruling — session
      // operator, không phải @cto — quyết định override SPEC-0417 §3.5.2 CR-4
      // "hard error with full-state reset to pristine + toast" cho trục
      // org_id): auto re-fetch + ghi đè editForm/editUser TRÊN LỖI là UX xấu —
      // xoá cả những field KHÔNG liên quan lỗi (vd full_name) mà TA vừa gõ
      // dở. Ruling nguyên văn: "Modal vẫn mở, thông tin vẫn giữ, ép người
      // dùng thao tác 'Hủy' mới là hành vi bình thường nhất". Cả 2 trục lỗi
      // (org_id cascade VÀ role/department/position) giờ hội tụ về CÙNG một
      // hành vi: giữ nguyên editForm/editUser (dirty state), modal không
      // đóng, KHÔNG re-fetch — chỉ khác nhau ở THÔNG ĐIỆP lỗi hiển thị (vẫn
      // phân biệt trục để câu chữ rõ ràng hơn cho TA, không phải để đổi
      // state). KHÔNG sửa SPEC-0417 — reconciliation tài liệu là việc của
      // người ra quyết định, không phải @coder.
      // Test: frontend/tests/13-tenant-admin/
      // TenantUserManagement.editOrgAxisErrorPreservesFormState.test.tsx.
      if (isOrgAxisFailure) {
        // Kênh toast dùng chung với savedToast (đã trung tính màu — #1f2937 —
        // phù hợp cả thông báo thành công lẫn lỗi, xem render phía dưới cùng
        // import/export toast) — không dựng kênh song song mới. Giữ thông
        // điệp riêng cho trục org_id (rõ ràng hơn "Cập nhật thất bại" chung
        // chung) nhưng KHÔNG còn kèm reset state.
        setSavedToast(
          "Lỗi cập nhật đơn vị — Không thể cập nhật đơn vị. Phiên đăng nhập có thể đã hết hạn."
        );
        setEditLoading(false);
        return;
      }

      // Trục role/department/position: giữ NGUYÊN hành vi cũ (field vẫn
      // dirty, modal vẫn mở với đúng những gì TA đang sửa dở).
      setEditError(extractErrorMessage(err, "Cập nhật thất bại"));
      setEditLoading(false);
      return;
    }

    // Khối [10d]: đã ghép hồ sơ → PATCH cập nhật; chưa ghép (checkbox tick) →
    // POST late-pairing. EMP-CODE-DUP (R4) trả 409 → banner [11b] — nhưng
    // PATCH TenantUser ở trên ĐÃ thành công, nên lỗi ở đây KHÔNG được hiện
    // như "Cập nhật thất bại" chung chung (banner list-level, modal đã đóng).
    if (profilePayload) {
      try {
        if (editHasProfile) {
          await apiClient.patch(`/tenant-users/${editUser.id}/employee-profile`, profilePayload);
          // BS-007-R29 STATE SAVED-PROFILE (@ba, đúng nguyên văn ratified):
          // "Submit modal Sửa khi hồ sơ ĐÃ ghép, PATCH employee-profile
          // thành công → toast 'Đã cập nhật hồ sơ nhân viên'".
          setSavedToast("Đã cập nhật hồ sơ nhân viên");
        } else {
          await apiClient.post(`/tenant-users/${editUser.id}/employee-profile`, profilePayload);
          // 🆕 (2026-08-25, @tester verify screenshot — Fix 2): checkbox "Bổ
          // sung hồ sơ nhân viên ngay" (late-pairing, POST lần đầu — KHÁC case
          // PATCH ở trên) trước đây đóng modal IM LẶNG dù comment tại khai báo
          // savedToast đã nói "late-pairing thành công cũng dùng kênh toast
          // này ('Đã bổ sung hồ sơ nhân viên')" nhưng chưa nối dây. R29 chỉ
          // ratify nguyên văn cho case PATCH (đã ghép) — case POST lần đầu
          // dùng đúng câu đã ghi sẵn trong comment gốc (không tự bịa văn án
          // mới), phân biệt "bổ sung" (lần đầu) với "cập nhật" (đã có, sửa lại).
          setSavedToast("Đã bổ sung hồ sơ nhân viên");
        }
      } catch (err: any) {
        setShowEdit(false);
        setEditUser(null);
        setEditHasProfile(null);
        setWantsPairing(false);
        setPairForm(INITIAL_PAIR_FORM);
        setEditLoading(false);
        // Issue #715 (BOT-004(a), @cto P1, 2026-09-02) — REAL repro qua
        // Playwright + backend thật (không chỉ đọc code tĩnh): banner này
        // được set nhưng KHÔNG BAO GIỜ lên màn hình. Nguyên nhân: fetchUsers()
        // là async function, dòng ĐẦU TIÊN của nó là setError("") (chạy
        // TRƯỚC await bên trong) — gọi fetchUsers() ở đây KHÔNG await, nên
        // setError("") của nó chạy ĐỒNG BỘ, CÙNG tick JS với setError(msg)
        // ngay bên trên. React 18 batch cả 2 lời gọi setError trong 1 lần
        // render — giá trị CUỐI CÙNG ("" từ fetchUsers) thắng, msg bị xoá
        // trước khi kịp vẽ ra màn hình dù request ĐÃ lỗi thật (thấy trên
        // Network tab F12) — đúng nguyên văn "UI hoàn toàn im lặng, chỉ thấy
        // qua F12". Tái hiện được với MỌI dạng lỗi (network abort, 500 text
        // thuần, 409/422 JSON) — không phải do response.data.detail bất
        // thường (extractErrorMessage đã kiểm chứng an toàn với mọi hình
        // dạng), mà do race giữa 2 lời gọi setState cùng tick.
        // Fix: await fetchUsers() TRƯỚC, setError(msg) SAU — đảm bảo banner
        // luôn là cập nhật state CUỐI CÙNG nên chắc chắn được render.
        await fetchUsers();
        // Nối lý do cụ thể từ backend (vd EMP-CODE-DUP 409) vào SAU câu ngữ
        // cảnh — không nuốt lý do thật vào 1 fallback chung chung, nhưng vẫn
        // giữ rõ phần cơ bản ĐÃ lưu (khác hẳn banner lỗi thông thường).
        setError(
          `Đã lưu thông tin cơ bản, nhưng chưa lưu được hồ sơ nhân viên — ${extractErrorMessage(err, "thử lại phần hồ sơ")}`
        );
        return;
      }
    }

    setShowEdit(false);
    setEditUser(null);
    setEditHasProfile(null);
    setWantsPairing(false);
    setPairForm(INITIAL_PAIR_FORM);
    setEditLoading(false);
    fetchUsers();
  };

  // BS-007-R19(a): người dùng xác nhận đổi Email → duyệt cờ rồi chạy lại submit
  const handleEmailChangeConfirm = () => {
    emailChangeApproved.current = true;
    setShowEmailChangeConfirm(false);
    handleEditUser({ preventDefault: () => {} } as React.FormEvent);
  };

  // ── WP-8b SC-007 GAP-2: Đặt lại mật khẩu [13] → [13b] ──────────────────
  const openResetDialog = (userId: string, userName: string) => {
    setResetTarget({ id: userId, name: userName });
    setResetError("");
  };

  const handleResetConfirm = async () => {
    if (!resetTarget) return;
    setResetLoading(true);
    setResetError("");
    try {
      // Body rỗng → BE tự sinh mật khẩu tạm + cờ must_change_password,
      // trả temporary_password đúng MỘT LẦN (mockup [13b])
      const res = await apiClient.post(`/tenant-users/${resetTarget.id}/reset-password`, {});
      const tempPassword: string | undefined = res.data?.temporary_password;
      setResetResult({ name: resetTarget.name, password: tempPassword || "" });
      setResetTarget(null);
      setPwdCopied(false);
    } catch (err: any) {
      setResetError(extractErrorMessage(err, "Đặt lại mật khẩu không thành công. Thử lại"));
    } finally {
      setResetLoading(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!resetResult?.password) return;
    try {
      if (navigator.clipboard) await navigator.clipboard.writeText(resetResult.password);
    } catch {
      // clipboard không khả dụng (http) — người dùng tự chép tay
    }
    setPwdCopied(true);
    setTimeout(() => setPwdCopied(false), 1500);
  };

  // ── ISS-0018 #2: [9] Xuất ▼ → menu [9m] — xuất danh sách theo bộ lọc đang áp ──
  const handleExport = async (format: "xlsx" | "csv") => {
    setExporting(true);
    try {
      // Lấy toàn bộ danh sách khớp bộ lọc (bỏ phân trang) — registry [9]: "xuất
      // danh sách theo bộ lọc đang áp". Backend chưa có endpoint export → sinh
      // tệp client-side (xlsx lib có sẵn trong dependencies).
      // ISS-PILOT-22 (2026-08-09): page_size=1000 vượt giới hạn backend
      // (Query(..., le=100), tenant_users.py:716) → 422 vô điều kiện, catch
      // nuốt lỗi thành toast "Xuất không thành công" mơ hồ, KHÔNG có file nào
      // tải về — tái hiện kể cả tenant chỉ có 1 user. Đổi sang loop phân
      // trang page_size=100 (trần backend cho phép) thay vì chỉ hạ số xuống
      // ≤100 — tránh cắt cụt xuất thiếu khi tenant thật có >100 user.
      const params: Record<string, any> = { page_size: 100 };
      if (searchQuery) params.search = searchQuery;
      if (orgFilter) params.organization_id = orgFilter;
      const rows: TenantUser[] = [];
      let page = 1;
      // Chặn vòng lặp vô hạn nếu backend trả sai total_pages — 500 trang
      // (50,000 user) đủ dư cho mọi tenant thật, không phải giới hạn nghiệp vụ.
      const MAX_EXPORT_PAGES = 500;
      while (page <= MAX_EXPORT_PAGES) {
        const res = await apiClient.get("/tenant-users/list", { params: { ...params, page } });
        const pageUsers: TenantUser[] = res.data?.users || [];
        rows.push(...pageUsers);
        const totalPages = res.data?.total_pages ?? 1;
        if (page >= totalPages || pageUsers.length === 0) break;
        page += 1;
      }
      // ISS-PILOT-22 REOPEN (2026-08-10, Hang Le): file xuất trước đây THIẾU
      // "Số điện thoại" (hiện dưới Họ tên trong bảng chính, registry L1319) và
      // "Email" (hiện trong modal Sửa — chính là định danh đăng nhập theo
      // BS-007-R03/R04) so với những gì UI màn Người dùng thực sự hiển thị.
      // Đối chiếu đầy đủ cột bảng chính + field modal Sửa (detail view) rồi bổ
      // sung 2 field còn thiếu — KHÔNG đổi/bỏ field cũ (thuần additive).
      const data = rows.map((u) => ({
        "Mã": u.employee_code || "",
        "Họ tên": u.full_name,
        "Số điện thoại": u.phone_number || "",
        "Email": u.email || "",
        "Tên đăng nhập": u.username,
        "Vai trò": roleColumnLabel(u),
        "Đơn vị": orgCodeNameLabel(u.organization_id),
        "Trạng thái": u.is_active ? "Đang hoạt động" : "Vô hiệu",
      }));
      const stamp = new Date().toISOString().slice(0, 10);
      if (format === "xlsx") {
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Nguoi dung");
        XLSX.writeFile(wb, `nguoi-dung-${stamp}.xlsx`);
      } else {
        const headers = Object.keys(data[0] || {
          "Mã": 1, "Họ tên": 1, "Số điện thoại": 1, "Email": 1, "Tên đăng nhập": 1,
          "Vai trò": 1, "Đơn vị": 1, "Trạng thái": 1,
        });
        const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
        const csv = "\uFEFF" + [headers.join(","), ...data.map((r) => headers.map((h) => escape((r as Record<string, string>)[h] ?? "")).join(","))].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `nguoi-dung-${stamp}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
      setExportMenuOpen(false);
    } catch {
      // STATE EXPORT-ING (registry L1352): lỗi → toast "Xuất không thành công. Thử lại"
      setExportToast("Xuất không thành công. Thử lại");
    } finally {
      setExporting(false);
    }
  };

  // ── WP-8b SC-007 GAP-3: Nhập người dùng [14] → [15] ─────────────────
  const postImportFile = async (endpoint: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    // Content-Type undefined → axios tự gắn multipart boundary
    return apiClientV2.post(endpoint, formData, {
      headers: { "Content-Type": undefined },
    });
  };

  const openImportModal = () => {
    setImportFile(null);
    setImportError("");
    setShowImportModal(true);
  };

  // Cùng gap/cùng fix pattern với bot-008 (OrganizationManagement/index.tsx):
  // modal [14] chỉ có text liệt kê cột, không có file mẫu để tải — TA phải tự
  // đoán format + tra thủ công Mã vai trò/Mã đơn vị hợp lệ. Sinh template
  // client-side (thư viện XLSX đã import sẵn ở đầu file, orgFlat đã load sẵn
  // trong state trang này — không cần endpoint mới).
  const handleDownloadUserImportTemplate = () => {
    // 🔄 (2026-08-25, @tester verify screenshot — đồng bộ thứ tự cột với
    // modal Thêm + backend `order` list trong user_import.py, xem comment
    // đầy đủ tại dòng gợi ý "Cột yêu cầu:" bên dưới trong JSX): Email đặt
    // TRƯỚC Số điện thoại (khớp modal: Họ tên*→Email*→Mật khẩu*→SĐT*→Vai
    // trò*→Đơn vị*, bỏ Mật khẩu); "Mã nhân viên"/"Ngày vào làm" đặt TRƯỚC
    // "Nhãn" (khớp khối [10d]: Mã nhân viên*→Ngày vào làm*→Chức danh). Tên
    // cột PHẢI khớp CHÍNH XÁC alias backend (_HEADER_ALIASES: "ma nhan
    // vien"/"ngay vao lam"/"nhan (tuy chon)" sau _norm_header bỏ dấu+lowercase)
    // — "Nhãn" KHÔNG đổi thành "Chức danh" dù R11 gọi field này là Chức danh
    // (backend alias chưa cập nhật, đổi tên ở đây sẽ làm cột rỗng âm thầm).
    // Cả "Mã nhân viên"+"Ngày vào làm" optional — để trống hàng ví dụ vì
    // AND-gate nghĩa là "để trống cả 2 = tương thích ngược, chỉ tạo
    // TenantUser", không phải lỗi; TA tự điền khi muốn dual-create Employee.
    const wsMain = XLSX.utils.json_to_sheet([
      {
        "Họ tên": "Nguyễn Văn Mẫu",
        "Email": "nguyenvanmau@example.com",
        "Số điện thoại": "0901234567",
        "Mã vai trò": "EMPLOYEE",
        "Mã đơn vị": orgFlat[0]?.code || "",
        "Mã nhân viên": "NV0001", // BS-007-R12 amend 2026-10-04: bắt buộc, là khoá đối chiếu
        "Ngày vào làm": "",
        "Nhãn (tùy chọn)": "",
      },
    ]);
    const wsRoles = XLSX.utils.json_to_sheet([
      { "Mã vai trò": "ADMIN", "Diễn giải": "Quản trị viên (miễn gắn đơn vị)" },
      { "Mã vai trò": "MANAGER", "Diễn giải": "Quản lý chi nhánh" },
      { "Mã vai trò": "EMPLOYEE", "Diễn giải": "Nhân viên" },
    ]);
    const wsUnits = XLSX.utils.json_to_sheet(
      orgFlat.map((n) => ({ "Mã đơn vị": n.code, "Tên đơn vị": n.name }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsMain, "Nhap nguoi dung");
    XLSX.utils.book_append_sheet(wb, wsRoles, "Ma vai tro hop le");
    XLSX.utils.book_append_sheet(wb, wsUnits, "Ma don vi hop le");
    XLSX.writeFile(wb, "template-nhap-nguoi-dung.xlsx");
  };

  // [14] → [Xem trước] → POST import/preview (không ghi DB) → mở modal [15]
  const handleImportPreview = async () => {
    if (!importFile) return;
    setPreviewing(true);
    setImportError("");
    try {
      const res = await postImportFile("/api/v2/tenant-admin/users/import/preview", importFile);
      setImportPreview(res.data);
      setShowImportModal(false);
    } catch (err: any) {
      setImportError(extractErrorMessage(err, "Không đọc được tệp. Kiểm tra lại định dạng."));
    } finally {
      setPreviewing(false);
    }
  };

  // [15] → [Nhập N dòng hợp lệ] → POST import/commit → toast + reload danh sách
  const handleImportCommit = async () => {
    if (!importFile || !importPreview) return;
    setCommitting(true);
    try {
      const res = await postImportFile(
        `/api/v2/tenant-admin/users/import/commit${confirmReview ? "?confirm_review=true" : ""}`,
        importFile,
      );
      setConfirmReview(false);
      // #1270 review: tên đăng nhập THẬT của tài khoản mới (không phải lúc nào cũng
      // = Mã NV viết thường) → tải về để TA trao cho từng người. Không có mật khẩu.
      const created: { row: number; employee_code: string; username: string }[] =
        res.data?.created_accounts ?? [];
      if (created.length > 0) {
        const ws = XLSX.utils.json_to_sheet(created.map((c) => ({
          "Dòng": c.row, "Mã nhân viên": c.employee_code, "Tên đăng nhập": c.username,
        })));
        const wbCreated = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wbCreated, ws, "Tai khoan moi");
        XLSX.writeFile(wbCreated, "tai-khoan-moi-ten-dang-nhap.xlsx");
      }
      setImportPreview(null);
      setImportFile(null);
      setImportToast(formatUserImportCommitToast(
        res.data?.imported ?? 0,
        res.data?.updated ?? 0,
        res.data?.employee_profile_conflicts ?? []
      ));
      fetchUsers();
    } catch (err: any) {
      setImportPreview(null);
      setImportFile(null);
      setImportToast(extractErrorMessage(err, "Lỗi nhập người dùng — thử lại"));
    } finally {
      setCommitting(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      {/* Content — ISS-0016 #5: header tổ chức chỉ nằm trong sidebar (TenantAdminLayout);
          vùng nội dung chỉ có tiêu đề vùng + nhóm nút ngang hàng đẩy phải (D3) */}
      {/* 2026-08-25 (UI-UX-DECISION-REGISTER.md luật 9, rollout): bỏ maxWidth
          1200px + margin:auto — cap cố định để lại lề chết 240px MỖI bên ở
          1920px (đo thật), không co giãn theo bề rộng màn hình. */}
      <main style={{ padding: '24px' }}>
        {/* [7] Tiêu đề vùng nội dung + [8] Nhập + [9] Xuất ▼ + [4] + Thêm người dùng */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
            Người dùng
          </h1>
          {capabilities.includes("TENANT_USER_MANAGE") && (
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* WP-8b SC-007 GAP-3 [8] Nhập → modal [14] → [15] */}
              {/* ISS-PILOT-32: inline hex → CSS-module + token, khớp Manager */}
              <button
                type="button"
                onClick={openImportModal}
                className={`${styles.btn} ${styles.btnSecondary}`}
              >
                Nhập
              </button>
              {/* ISS-0018 #2: [9] Xuất ▼ → menu [9m] */}
              <div style={{ position: 'relative' }} onMouseDown={(e) => e.stopPropagation()}>
                {/* ISS-PILOT-32: inline hex → CSS-module + token, khớp Manager */}
                <button
                  type="button"
                  onClick={() => setExportMenuOpen((v) => !v)}
                  disabled={exporting}
                  className={`${styles.btn} ${styles.btnSecondary}`}
                >
                  {exporting ? 'Đang xuất…' : 'Xuất ▼'}
                </button>
                {exportMenuOpen && (
                  <div style={{
                    position: 'absolute', right: 0, top: '100%', marginTop: '4px', zIndex: 100,
                    background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '160px', overflow: 'hidden',
                  }}>
                    <button onClick={() => handleExport('xlsx')} style={menuItemStyle}>
                      Xuất Excel (.xlsx)
                    </button>
                    <button onClick={() => handleExport('csv')} style={menuItemStyle}>
                      Xuất CSV (.csv)
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={openCreateModal}
                style={{
                  padding: '8px 20px',
                  background: PRIMARY,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                + Thêm người dùng
              </button>
            </div>
          )}
        </div>

        {/* Toolbar 1 hàng ngang (D4): [1] tìm theo tên + [2] lọc theo đơn vị + [3] đếm đẩy phải */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '20px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <input
            type="text"
            placeholder="Tìm theo tên…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              minWidth: '200px',
            }}
          />
          {/* ISSUE-021: AnchorPicker subtree filter — [2] Lọc theo đơn vị */}
          <div style={{ minWidth: '240px' }}>
            <AnchorPicker
              value={orgFilter}
              onChange={(val) => { setOrgFilter(val); setPage(1); }}
              label="Lọc theo đơn vị"
            />
          </div>
          {/* BR-EMP-003 (SC-007 amendment) [2b]: Lọc Hồ sơ NV — nhóm TRÁI ngay sau
              [2]; 3 giá trị Tất cả / Đủ hồ sơ / Thiếu hồ sơ; mặc định "Tất cả" */}
          <select
            value={profileFilter}
            onChange={(e) => { setProfileFilter(e.target.value); setPage(1); }}
            aria-label="Hồ sơ NV"
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
              color: '#374151',
            }}
          >
            <option value="">Hồ sơ NV: Tất cả</option>
            <option value="linked">Hồ sơ NV: Đủ hồ sơ</option>
            <option value="missing">Hồ sơ NV: Thiếu hồ sơ</option>
          </select>
          <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: '14px', color: '#6b7280' }}>
            {total} người dùng
          </span>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            color: '#dc2626',
            fontSize: '14px',
          }}>
            {error}
          </div>
        )}

        {/* ISS-PILOT-12 (2026-08-09, @ba quyết định): thêm cột "Mã" (employee_code),
            đặt TRƯỚC cột Họ tên (thứ tự Mã → Tên, đúng quy tắc cột chính §ISS-PILOT-8). */}
        {/* [5] Bảng 7 cột: Mã · Họ tên · Vai tròⓘ · Đơn vịⓘ · Trạng tháiⓘ · 🆕 Hồ sơ NVⓘ · Thao tác
            (BR-EMP-003 SC-007 amendment — cột chip [5b] GIỮA Trạng thái và Thao tác) */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          // BR-EMP-003 RESPONSIVE (tablet 768–1023 R5): thêm cột thứ 7 → bảng giữ
          // cột, cuộn ngang thay vì ép vỡ layout
          overflowX: 'auto',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '840px' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={thStyle}>Mã</th>
                <th style={thStyle}>Họ tên</th>
                <th style={thStyle}>
                  Vai trò
                  {/* ISS-PILOT-30 (2026-08-12, PO chốt): rút gọn tooltip — chỉ mô tả
                      nội dung cột, bỏ giải thích cách suy luận cấp bậc. */}
                  <PortalTooltip text="Vai trò hệ thống của người dùng và tên hiển thị tương ứng." tabIndex={0} ariaLabel="Vai trò hệ thống của người dùng và tên hiển thị tương ứng.">
                    <InfoIcon />
                  </PortalTooltip>
                </th>
                <th style={thStyle}>
                  Đơn vị
                  <PortalTooltip text="Mã đơn vị và tên hiển thị được gán cho người dùng." tabIndex={0} ariaLabel="Mã đơn vị và tên hiển thị được gán cho người dùng.">
                    <InfoIcon />
                  </PortalTooltip>
                </th>
                <th style={thStyle}>
                  Trạng thái
                  <PortalTooltip text="Đang hoạt động: đăng nhập được. Vô hiệu: không đăng nhập được cho tới khi kích hoạt lại." tabIndex={0} ariaLabel="Đang hoạt động: đăng nhập được. Vô hiệu: không đăng nhập được cho tới khi kích hoạt lại.">
                    <InfoIcon />
                  </PortalTooltip>
                </th>
                {/* BR-EMP-003 [5b]: cột chip Hồ sơ NV — read-only */}
                <th style={thStyle}>
                  Hồ sơ NV
                  <PortalTooltip text="Người dùng đã có hồ sơ nhân viên (chấm công/lương) liên kết chưa. Bắt buộc với vai trò đi làm (BR-EMP-003-R2)." tabIndex={0} ariaLabel="Người dùng đã có hồ sơ nhân viên (chấm công/lương) liên kết chưa. Bắt buộc với vai trò đi làm (BR-EMP-003-R2).">
                    <InfoIcon />
                  </PortalTooltip>
                </th>
                <th style={{ ...thStyle, textAlign: 'center', width: '72px' }}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    Đang tải...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    Không có người dùng nào
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    {/* ISS-PILOT-12 (2026-08-09): "—" khi TA chưa gán (field optional) */}
                    <td style={tdStyle}>
                      <span style={{ fontSize: '13px', color: u.employee_code ? '#374151' : '#9ca3af' }}>
                        {u.employee_code || "—"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: '500', color: '#1f2937' }}>{u.full_name}</div>
                      {u.phone_number && (
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{u.phone_number}</div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {/* BA-D-003 rule 2 + (d)3: không bao giờ render mã role thô;
                          TENANT_OWNER hiển thị "Quản trị viên". ISS-PILOT-10
                          (2026-08-09, 🔄⁴ retrofit 2026-08-25): kèm employee_position
                          (employees.position, text tự do TA gõ ở khối [10d], không
                          phải mã role) khi user đã ghép hồ sơ — "<nhãn nhóm> -
                          <employee_position>", phân biệt BRANCH_MANAGER/SHIFT_MANAGER/
                          HEAD_CHEF thay vì cả 3 hiện y hệt "Quản lý". */}
                      <span style={{
                        padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                        background: ADMIN_ROLE_VALUES.has(u.role) ? PRIMARY_SOFT_BG : '#f3f4f6',
                        color: ADMIN_ROLE_VALUES.has(u.role) ? PRIMARY : '#374151',
                      }}>
                        {roleColumnLabel(u)}
                      </span>
                    </td>
                    {/* ISS-PILOT-8 (2026-08-09, @ba quyết định): cột "Đơn vị" là cột
                        THAM CHIẾU (trỏ sang cây cơ cấu, không phải chủ thể chính của
                        màn Người dùng) → format thống nhất "<Mã> - <Tên>", không chỉ
                        mã thô như trước (registry L1319/ISS-0018 #3, nay thay).
                        BA-D-001 rule 2: user chưa gán hiển thị "—" */}
                    <td style={tdStyle}>
                      {u.organization_id ? (
                        <span style={{ fontSize: '13px', color: '#374151' }}>
                          {orgCodeNameLabel(u.organization_id)}
                        </span>
                      ) : (
                        <span style={{ fontSize: '13px', color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {/* Registry L1127: chỉ 2 trạng thái (Đang hoạt động / Vô hiệu) */}
                      <span style={{
                        padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                        background: u.is_active ? '#dcfce7' : '#fef2f2',
                        color: u.is_active ? '#16a34a' : '#dc2626',
                      }}>
                        {u.is_active ? "● Đang hoạt động" : "○ Vô hiệu"}
                      </span>
                    </td>
                    {/* BR-EMP-003 [5b]: chip trạng thái pairing — read-only (R6
                        link/unlink KHÔNG thuộc v0). ✓ Đủ / ! Thiếu / — Không cần */}
                    <td style={tdStyle}>
                      {(() => {
                        const chip = employeeProfileChip(u);
                        const chipStyle: React.CSSProperties = {
                          padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                          whiteSpace: 'nowrap',
                        };
                        if (chip === "complete") {
                          return (
                            <span style={{ ...chipStyle, background: '#dcfce7', color: '#16a34a' }}>
                              ✓ Đủ hồ sơ
                            </span>
                          );
                        }
                        if (chip === "missing") {
                          return (
                            <span style={{ ...chipStyle, background: '#fef3c7', color: '#b45309' }}>
                              ! Thiếu hồ sơ
                            </span>
                          );
                        }
                        return (
                          <span style={{ ...chipStyle, background: '#f3f4f6', color: '#9ca3af' }}>
                            — Không cần
                          </span>
                        );
                      })()}
                    </td>
                    {/* ⁝ menu từng dòng (registry L1323, D5): Sửa · Đặt lại mật khẩu ·
                        Vô hiệu hóa/Kích hoạt — KHÔNG có Lưu trữ (L1127) */}
                    <td style={{ ...tdStyle, textAlign: 'center', position: 'relative' }}>
                      {capabilities.includes("TENANT_USER_MANAGE") && (
                        <div style={{ display: 'inline-block', position: 'relative' }} onMouseDown={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => {
                              if (menuOpenId === u.id) { setMenuOpenId(null); return; }
                              const r = e.currentTarget.getBoundingClientRect();
                              setMenuAnchor({ top: r.top, bottom: r.bottom, right: window.innerWidth - r.right });
                              setMenuOpenId(u.id);
                            }}
                            aria-label={`Thao tác cho ${u.full_name}`}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              fontSize: '18px', color: '#6b7280', padding: '2px 8px',
                            }}
                          >
                            ⁝
                          </button>
                          {menuOpenId === u.id && menuAnchor && (
                            <PortalMenu anchor={menuAnchor} onClose={() => setMenuOpenId(null)}>
                              <button
                                onClick={() => { setMenuOpenId(null); openEditModal(u); }}
                                style={menuItemStyle}
                              >
                                Sửa
                              </button>
                              {/* BR-EMP-003 (2026-08-24 gộp cửa sổ): KHÔNG còn item
                                  "Bổ sung hồ sơ NV"/"Sửa hồ sơ NV" ở ⁝ — hồ sơ NV
                                  sửa ngay trong modal Sửa (khối [10d]); chip ! Thiếu
                                  → mở Sửa là ra form bổ sung late-pairing */}
                              <button
                                onClick={() => { setMenuOpenId(null); openResetDialog(u.id, u.full_name); }}
                                style={menuItemStyle}
                              >
                                Đặt lại mật khẩu
                              </button>
                              {u.is_active ? (
                                <button
                                  onClick={() => { setMenuOpenId(null); setDeactivateTarget({ id: u.id, name: u.full_name }); }}
                                  style={{ ...menuItemStyle, color: '#dc2626' }}
                                >
                                  Vô hiệu hóa
                                </button>
                              ) : (
                                <button
                                  onClick={() => { setMenuOpenId(null); handleActivate(u.id); }}
                                  style={{ ...menuItemStyle, color: '#16a34a' }}
                                >
                                  Kích hoạt
                                </button>
                              )}
                            </PortalMenu>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* [6] Phân trang — «Trước · Trang X/Y · Sau», ẩn khi chỉ 1 trang */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '20px',
          }}>
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              style={paginationBtn(page <= 1)}
            >
              &laquo; Trước
            </button>
            <span style={{ alignSelf: 'center', fontSize: '14px', color: '#6b7280' }}>
              Trang {page}/{totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              style={paginationBtn(page >= totalPages)}
            >
              Sau &raquo;
            </button>
          </div>
        )}
      </main>

      {/* [10] MODAL "Thêm người dùng" — giữa màn ~480px, nhãn trên, 1 field/dòng */}
      {showCreate && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '100%',
            maxWidth: '480px',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Thêm người dùng
              </h2>
              {/* D6: modal có [✕] góc trên */}
              <button
                onClick={() => { setShowCreate(false); setCreateError(""); }}
                aria-label="Đóng"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#6b7280', padding: '2px' }}
              >
                ✕
              </button>
            </div>

            {/* [10b] banner lỗi — ẩn khi rỗng */}
            {createError && (
              <div ref={createErrorRef} style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '14px',
              }}>
                {createError}
              </div>
            )}

            <form onSubmit={handleCreateUser}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Họ tên *</label>
                  <input
                    required
                    value={createForm.full_name}
                    onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                    style={inputStyle}
                    placeholder="Nguyễn Văn A"
                  />
                </div>
                {/* ISS-PILOT-12 + BR-EMP-003 (SC-007 amendment): "Mã nhân viên"
                    đã CHUYỂN vào khối [10d] "Hồ sơ nhân viên" bên dưới (hiện khi
                    Vai trò thuộc nhóm đi làm — R2; ẩn với Quản trị viên). */}
                {/* BS-007-R03/R04 (BA-D-008): Email = định danh đăng nhập, đứng ngay sau
                    Họ tên; KHÔNG còn ô Tên đăng nhập — mã người dùng do hệ thống sinh */}
                <div>
                  <label style={labelStyle}>Email *</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    style={inputStyle}
                    placeholder="email@company.com"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Mật khẩu *</label>
                  <input
                    required
                    type="password"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    style={inputStyle}
                    placeholder="Ít nhất 8 ký tự"
                    minLength={8}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Số điện thoại *</label>
                  <input
                    required
                    value={createForm.phone_number}
                    onChange={(e) => setCreateForm({ ...createForm, phone_number: e.target.value })}
                    style={inputStyle}
                    placeholder="0901234567"
                    pattern="^(\+84|0)[0-9]{9,10}$"
                  />
                </div>
                {/* BA-D-002: 3 nhóm gốc, nhãn VI nguyên văn, placeholder "Chọn vai trò".
                    ISS-PILOT-10 REOPEN (2026-08-10): + 2 lựa chọn tường minh Trưởng ca
                    (SHIFT_MANAGER)/Bếp trưởng (HEAD_CHEF) — 2 role đã tồn tại ở backend
                    và đã dùng qua Nhập Excel (ISS-PILOT-23 §13) nhưng chưa chọn được ở
                    đây; "Quản lý" giữ nguyên hành vi suy theo cấp Đơn vị cho
                    CORPORATE_MANAGER/BRANCH_MANAGER/SHIFT_MANAGER như cũ. */}
                <div>
                  <label style={labelStyle}>Vai trò *</label>
                  <select
                    required
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as RoleSelection })}
                    style={inputStyle}
                  >
                    <option value="" disabled>Chọn vai trò</option>
                    <option value="ADMIN">Quản trị viên</option>
                    <option value="MANAGER">Quản lý</option>
                    <option value="SHIFT_MANAGER">Trưởng ca</option>
                    <option value="HEAD_CHEF">Bếp trưởng</option>
                    <option value="EMPLOYEE">Nhân viên</option>
                  </select>
                </div>

                {/* Đơn vị * (cây tổ chức) — DD-0051 D1 anchor; BS-007-R03: bắt buộc
                    với mọi vai trò khác Quản trị viên.
                    ISS-0017 #3: DepartmentPicker đã GỠ theo registry L1127 (v0 không
                    có khái niệm "phòng ban" riêng — work_area là một cấp trong cây). */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <AnchorPicker
                    value={createForm.organization_id}
                    onChange={(orgId: string) =>
                      setCreateForm({ ...createForm, organization_id: orgId })
                    }
                    required={createForm.role !== "" && createForm.role !== "ADMIN"}
                    disabled={createLoading}
                    /* DD-0052 AC-UX-2: enable inline create-org shortcut.
                       Side-panel overlay preserves Create User form state. */
                    enableInlineCreate={true}
                  />
                </div>
                {/* 🔄⁴ (2026-08-25): field "Nhãn hiển thị" riêng đã BỎ — gộp vào
                    Chức danh trong khối [10d] EmployeeProfileBlock ngay dưới đây. */}

                {/* BR-EMP-003 (SC-007 amendment) khối [10d] "Hồ sơ nhân viên":
                    HIỆN khi Vai trò thuộc nhóm đi làm (R2 — mọi lựa chọn trừ Quản
                    trị viên); ẨN với Quản trị viên kèm dòng ghi chú. Submit tạo
                    TenantUser + Employee + link trong 1 transaction (R3). */}
                {createForm.role !== "" && createForm.role !== "ADMIN" && (
                  <EmployeeProfileBlock
                    form={createForm}
                    onChange={(patch) => setCreateForm({ ...createForm, ...patch })}
                    positionSuggestion={positionSuggestionFor(
                      createForm.role,
                      orgLevelById.get(createForm.organization_id) ?? null
                    )}
                    positionTouched={createPositionTouched}
                    onPositionInput={(value) => {
                      setCreatePositionTouched(true);
                      setCreateForm({ ...createForm, position: value });
                    }}
                    onUseSuggestion={() => {
                      setCreatePositionTouched(false);
                      setCreateForm({
                        ...createForm,
                        position:
                          positionSuggestionFor(
                            createForm.role,
                            orgLevelById.get(createForm.organization_id) ?? null
                          ) ?? "",
                      });
                    }}
                  />
                )}
                {createForm.role === "ADMIN" && (
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                    Vai trò quản trị không yêu cầu hồ sơ nhân viên.
                  </p>
                )}
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '24px',
              }}>
                <button
                  type="button"
                  onClick={() => { setShowCreate(false); setCreateError(""); }}
                  style={{
                    padding: '8px 20px',
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  style={{
                    padding: '8px 20px',
                    background: createLoading ? '#9ca3af' : PRIMARY,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: createLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {createLoading ? 'Đang tạo...' : 'Tạo người dùng'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* [11] MODAL "Sửa người dùng" — registry L1329: KHÔNG có khối phân quyền bổ sung */}
      {showEdit && editUser && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '100%',
            maxWidth: '480px',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Sửa người dùng
              </h2>
              {/* D6: modal có [✕] góc trên */}
              <button
                onClick={() => { setShowEdit(false); setEditError(""); }}
                aria-label="Đóng"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#6b7280', padding: '2px' }}
              >
                ✕
              </button>
            </div>

            {/* [11b] banner lỗi — ẩn khi rỗng */}
            {editError && (
              <div ref={editErrorRef} style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '14px',
              }}>
                {editError}
              </div>
            )}

            {/* BR-EMP-003 (2026-08-24 gộp cửa sổ): modal Sửa chứa khối [10d]
                (hiện với vai trò đi làm) — submit duy nhất qua handleEditUser:
                PATCH user + (PATCH|POST) employee-profile */}
            <form onSubmit={handleEditUser}>
              <fieldset
                style={{ border: 'none', margin: 0, padding: 0, minWidth: 0 }}
              >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Họ tên *</label>
                  <input
                    required
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                {/* BR-EMP-003 (SC-007 amendment): "Mã nhân viên" CHUYỂN vào khối
                    [10d] bên dưới (hiện khi Vai trò thuộc nhóm đi làm). */}
                {/* BS-007-R03/R04: Email đứng ngay sau Họ tên (định danh đăng nhập).
                    BS-007-R20 (BA-D-008): đã thiết lập → CHỈ ĐỌC ở v0; chỉ điền khi trống (backfill) */}
                <div>
                  <label style={labelStyle}>Email *</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    style={editUser.email
                      ? { ...inputStyle, background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }
                      : inputStyle}
                    readOnly={!!editUser.email}
                    placeholder="email@company.com"
                  />
                  {editUser.email && (
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                      Email đã được thiết lập và không thể thay đổi
                    </p>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Số điện thoại</label>
                  <input
                    value={editForm.phone_number}
                    onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                    style={inputStyle}
                    pattern="^(\+84|0)[0-9]{9,10}$"
                  />
                </div>
                {/* BA-D-002 rule 3: role ẩn snap về nhãn nhóm; Lưu chuẩn hoá role.
                    ISS-PILOT-10 REOPEN (2026-08-10): + Trưởng ca/Bếp trưởng tường minh
                    (xem ghi chú ở modal Thêm phía trên). */}
                <div>
                  <label style={labelStyle}>Vai trò *</label>
                  <select
                    required
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value as RoleSelection })}
                    style={inputStyle}
                  >
                    <option value="ADMIN">Quản trị viên</option>
                    <option value="MANAGER">Quản lý</option>
                    <option value="SHIFT_MANAGER">Trưởng ca</option>
                    <option value="HEAD_CHEF">Bếp trưởng</option>
                    <option value="EMPLOYEE">Nhân viên</option>
                  </select>
                </div>
                {/* Đơn vị * (cây tổ chức) — BA-D-001 rule 3: backfill thủ công qua
                    Sửa; vai trò khác Quản trị viên → bắt buộc khi Lưu (BS-007-R03) */}
                {/* BOT-002 fix: Quản trị viên được miễn anchor (cột Đơn vị = "—"
                    ở list) nhưng ô này vẫn cho chọn — vô tình đổi rồi Lưu bị 403
                    re-anchor (S102L-079) khó hiểu, dù ý định chỉ sửa hồ sơ. Ẩn
                    ô này cho ADMIN thay vì chỉ bỏ `required`; guardrail backend
                    giữ nguyên, đây thuần là UI. */}
                {editForm.role !== "ADMIN" ? (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <AnchorPicker
                      value={editForm.organization_id}
                      onChange={(orgId: string) =>
                        setEditForm({ ...editForm, organization_id: orgId })
                      }
                      required
                      disabled={editLoading}
                    />
                  </div>
                ) : (
                  <p style={{ gridColumn: '1 / -1', fontSize: '13px', color: '#6b7280', margin: 0 }}>
                    Vai trò Quản trị viên không gắn đơn vị.
                  </p>
                )}
                {/* 🔄⁴ (2026-08-25): field "Nhãn hiển thị" riêng đã BỎ — gộp vào
                    Chức danh trong khối [10d] EmployeeProfileBlock bên dưới. */}
              </div>
              </fieldset>

              {/* BR-EMP-003 (2026-08-24 gộp cửa sổ) + 🆕 checkbox (2026-08-25,
                  coordinator fix — KHÔNG có trong BS-007-R26/sc-007-draft.md
                  hiện tại, xem khai báo wantsPairing + báo cáo cuối):
                  đã ghép hồ sơ (editHasProfile true) → khối [10d] LUÔN hiện,
                  đúng R26 không đổi. CHƯA ghép (! Thiếu hồ sơ) → hiện checkbox
                  "Bổ sung hồ sơ nhân viên ngay"; KHÔNG tick (mặc định) → khối
                  ẨN, KHÔNG validate/block Lưu — TA sửa field khác (SĐT, Vai
                  trò…) lưu bình thường, chip giữ nguyên "! Thiếu hồ sơ". TICK
                  → khối hiện, employee_code/hire_date bắt buộc như thiết kế. */}
              {editForm.role !== "ADMIN" && editHasProfile === false && (
                <label style={{
                  display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px',
                  fontSize: '14px', cursor: 'pointer', userSelect: 'none',
                }}>
                  <input
                    type="checkbox"
                    checked={wantsPairing}
                    onChange={(e) => setWantsPairing(e.target.checked)}
                  />
                  Bổ sung hồ sơ nhân viên ngay
                </label>
              )}
              {shouldValidateEmployeeProfile(editForm.role, editHasProfile, wantsPairing) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                  <EmployeeProfileBlock
                    form={pairForm}
                    onChange={(patch) => setPairForm({ ...pairForm, ...patch })}
                    lockEmployeeCode={!!editHasProfile}
                    positionSuggestion={positionSuggestionFor(
                      editForm.role,
                      orgLevelById.get(editForm.organization_id) ?? null
                    )}
                    positionTouched={editPositionTouched}
                    onPositionInput={(value) => {
                      setEditPositionTouched(true);
                      setPairForm({ ...pairForm, position: value });
                    }}
                    onUseSuggestion={() => {
                      setEditPositionTouched(false);
                      setPairForm({
                        ...pairForm,
                        position:
                          positionSuggestionFor(
                            editForm.role,
                            orgLevelById.get(editForm.organization_id) ?? null
                          ) ?? "",
                      });
                    }}
                  />
                </div>
              )}
              {editForm.role === "ADMIN" && (
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '16px', marginBottom: 0 }}>
                  Vai trò quản trị không yêu cầu hồ sơ nhân viên.
                </p>
              )}

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '12px',
                marginTop: '24px',
              }}>
                {/* RS-3 (2026-07-30): nút phụ [Đặt lại mật khẩu] theo spec [11] L1241 — mở dialog [13] */}
                <button
                  type="button"
                  onClick={() => {
                    const target = { id: editUser.id, name: editForm.full_name };
                    setShowEdit(false);
                    openResetDialog(target.id, target.name);
                  }}
                  style={{
                    marginRight: 'auto',
                    padding: '8px 16px',
                    background: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151',
                  }}
                >
                  Đặt lại mật khẩu
                </button>
                <button
                  type="button"
                  onClick={() => { setShowEdit(false); setEditError(""); }}
                  style={{
                    padding: '8px 20px',
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  style={{
                    padding: '8px 20px',
                    background: editLoading ? '#9ca3af' : PRIMARY,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: editLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {editLoading ? 'Đang lưu...' : 'Lưu thay đổi'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════ RS-2: [12] DIALOG xác nhận Vô hiệu — giữa màn ~440px (registry L1247-1254) ══════════ */}
      {deactivateTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '32px',
            width: '100%', maxWidth: '440px',
          }} role="dialog" aria-modal="true" aria-labelledby="deactivateTitle">
            <h2 id="deactivateTitle" style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
              Vô hiệu hóa tài khoản?
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
              Tài khoản "{deactivateTarget.name}" sẽ không đăng nhập được cho tới khi được kích hoạt lại.
            </p>
            {/* BR-EMP-003-R7 + AC-4 (SC-007 amendment [12]): ARCHIVED → employee
                inactive, GIỮ lịch sử — wording xác nhận dữ liệu được giữ lại */}
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              Hồ sơ nhân viên và dữ liệu chấm công, lương của người này được giữ lại.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setDeactivateTarget(null)}
                disabled={deactivateLoading}
                style={{ padding: '8px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
              >
                Hủy
              </button>
              <button
                onClick={handleDeactivateConfirm}
                disabled={deactivateLoading}
                style={{
                  padding: '8px 20px', background: deactivateLoading ? '#9ca3af' : '#dc2626', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: deactivateLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '500',
                }}
              >
                {deactivateLoading ? 'Đang xử lý…' : 'Vô hiệu hóa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ BS-007-R19(a): DIALOG xác nhận đổi Email — đổi định danh đăng nhập ══════════ */}
      {showEmailChangeConfirm && editUser && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '32px',
            width: '100%', maxWidth: '440px',
          }} role="dialog" aria-modal="true" aria-labelledby="emailChangeTitle">
            <h2 id="emailChangeTitle" style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
              Đổi email đăng nhập?
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
              Người dùng sẽ đăng nhập bằng email mới. Email cũ không dùng để đăng nhập được nữa.
            </p>
            <p style={{ fontSize: '13px', color: '#374151', marginBottom: '20px', wordBreak: 'break-all' }}>
              {editUser.email || '—'} → {editForm.email.trim()}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setShowEmailChangeConfirm(false)}
                disabled={editLoading}
                style={{ padding: '8px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
              >
                Hủy
              </button>
              <button
                onClick={handleEmailChangeConfirm}
                disabled={editLoading}
                style={{
                  padding: '8px 20px', background: editLoading ? '#9ca3af' : PRIMARY, color: 'white',
                  border: 'none', borderRadius: '6px', cursor: editLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '500',
                }}
              >
                {editLoading ? 'Đang xử lý…' : 'Đổi email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ WP-8b SC-007 GAP-2 [13] DIALOG "Đặt lại mật khẩu" — xác nhận ══════════ */}
      {resetTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '32px',
            width: '100%', maxWidth: '440px',
          }} role="dialog" aria-modal="true" aria-labelledby="resetTitle">
            <style>{'@keyframes nqhSpin{to{transform:rotate(360deg)}}'}</style>
            <h2 id="resetTitle" style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
              Đặt lại mật khẩu?
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              Tài khoản "{resetTarget.name}" sẽ nhận một mật khẩu tạm và buộc đổi mật khẩu ở lần đăng nhập kế.
            </p>
            {resetError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px', marginBottom: '16px', color: '#dc2626', fontSize: '13px' }}>
                {resetError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setResetTarget(null)}
                disabled={resetLoading}
                style={{ padding: '8px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
              >
                Hủy
              </button>
              <button
                onClick={handleResetConfirm}
                disabled={resetLoading}
                style={{
                  padding: '8px 20px', background: resetLoading ? '#9ca3af' : PRIMARY, color: 'white',
                  border: 'none', borderRadius: '6px', cursor: resetLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '8px',
                }}
              >
                {resetLoading && (
                  <span style={{
                    width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: 'white', borderRadius: '50%', display: 'inline-block',
                    animation: 'nqhSpin 0.8s linear infinite',
                  }} aria-hidden="true" />
                )}
                Đặt lại mật khẩu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ WP-8b SC-007 GAP-2 [13b] KẾT QUẢ — mật khẩu tạm hiện MỘT LẦN ══════════ */}
      {resetResult && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '32px',
            width: '100%', maxWidth: '440px',
          }} role="dialog" aria-modal="true" aria-labelledby="resetDoneTitle">
            <h2 id="resetDoneTitle" style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
              ✔ Đã đặt lại mật khẩu — {resetResult.name}
            </h2>
            {resetResult.password ? (
              <>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>Mật khẩu tạm:</div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f9fafb',
                  padding: '12px 16px', margin: '8px 0',
                }}>
                  <span style={{ fontSize: '18px', fontWeight: 'bold', letterSpacing: '0.04em', color: '#1f2937' }}>
                    {resetResult.password}
                  </span>
                  <button
                    onClick={handleCopyPassword}
                    style={{ padding: '4px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                  >
                    {pwdCopied ? 'Đã chép' : 'Sao chép'}
                  </button>
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  Gửi cho người dùng; sẽ buộc đổi ở lần đăng nhập kế tiếp. Mật khẩu này chỉ hiện một lần.
                </div>
              </>
            ) : (
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                Mật khẩu đã được đặt lại; người dùng sẽ buộc đổi ở lần đăng nhập kế tiếp.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => setResetResult(null)}
                style={{ padding: '8px 20px', background: PRIMARY, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
              >
                Xong
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ WP-8b SC-007 GAP-3 [14] MODAL "Nhập người dùng" — bước 1 chọn tệp ══════════ */}
      {showImportModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '24px',
            width: '100%', maxWidth: '480px',
          }} role="dialog" aria-modal="true" aria-labelledby="importTitle">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <h2 id="importTitle" style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
                Nhập người dùng
              </h2>
              {/* D6: modal có [✕] góc trên */}
              <button
                onClick={() => setShowImportModal(false)}
                aria-label="Đóng"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#6b7280', padding: '2px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>Chọn tệp .xlsx hoặc .csv</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <button
                type="button"
                onClick={() => importFileRef.current?.click()}
                style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
              >
                Chọn tệp…
              </button>
              <span style={{ fontSize: '13px', color: '#374151' }}>
                {importFile ? importFile.name : 'Chưa chọn tệp'}
              </span>
              <input
                ref={importFileRef}
                type="file"
                accept=".xlsx,.csv"
                style={{ display: 'none' }}
                onChange={(e) => {
                  setImportFile(e.target.files?.[0] || null);
                  setImportError("");
                }}
              />
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>
              {/* 🔄 (2026-08-25, @tester verify screenshot — đồng bộ thứ tự 3
                  nơi: bảng ↔ modal Thêm ↔ tệp Import): ĐẢO LẠI thứ tự
                  SĐT/Email của ISS-PILOT-29 — dòng gợi ý này giờ khớp đúng
                  thứ tự field modal Thêm (Họ tên*→Email*→Mật khẩu*→SĐT*→Vai
                  trò*→Đơn vị*, bỏ Mật khẩu vì Import không áp dụng) VÀ thứ tự
                  cột thật backend đang parse (`order` trong user_import.py).
                  ISS-PILOT-29 (SĐT cạnh Họ tên) vẫn giữ nguyên RIÊNG cho bảng
                  Xem trước [15] bên dưới — đó là quy ước hiển thị khác, mô
                  phỏng cách bảng danh sách chính gộp SĐT dưới Họ tên trong
                  CÙNG 1 cột, không phải thứ tự cột tệp. Backend map theo TÊN
                  cột/alias, không theo vị trí — đổi thứ tự ở đây AN TOÀN,
                  không đụng logic parse (xem _HEADER_ALIASES ở
                  backend/app/api/v2/tenant_admin/user_import.py).
                  🆕 "Mã nhân viên"/"Ngày vào làm" (BS-007-R11, dual-create
                  AND-gate) — đặt TRƯỚC "Nhãn", khớp đúng vị trí khối [10d]
                  trong modal (Mã nhân viên*→Ngày vào làm*→Chức danh — "Nhãn"
                  ở cột Import chính là field Chức danh, R11: "đổi tên từ
                  'Nhãn'" nhưng backend _HEADER_ALIASES CHƯA cập nhật alias
                  chấp nhận "Chức danh" — vẫn CHỈ nhận đúng chuỗi "Nhãn"/"Nhãn
                  (tùy chọn)" làm tên cột, xem báo cáo cuối; ĐỂ NGUYÊN "Nhãn"
                  ở đây, KHÔNG đổi thành "Chức danh" — đổi sẽ làm cột đó
                  KHÔNG được nhận diện, âm thầm rỗng mọi dòng). Tên 2 cột mới
                  khớp CHÍNH XÁC alias backend ("ma nhan vien"/"ngay vao lam"
                  sau _norm_header). */}
              Cột yêu cầu: Họ tên · Email (tùy chọn) · Số điện thoại (tùy chọn) · Mã vai trò · Mã đơn vị · Mã nhân viên (khoá đối chiếu) · Ngày vào làm (tùy chọn) · Nhãn (tùy chọn)
            </div>
            <button
              type="button"
              onClick={handleDownloadUserImportTemplate}
              style={{ marginTop: '8px', padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
            >
              Tải template
            </button>
            {importError && (
              <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
                {importError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={() => setShowImportModal(false)}
                style={{ padding: '8px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
              >
                Hủy
              </button>
              <button
                onClick={handleImportPreview}
                disabled={!importFile || previewing}
                style={{
                  padding: '8px 20px', background: !importFile || previewing ? '#9ca3af' : PRIMARY,
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: !importFile || previewing ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '500',
                }}
              >
                {previewing ? 'Đang xem trước…' : 'Xem trước'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ WP-8b SC-007 GAP-3 [15] MODAL "Xem trước nhập" — bảng 8 cột
          (ISS-PILOT-29 2026-08-12: thêm cột SĐT + sắp lại thứ tự Họ tên·SĐT·Email
          cho gần thứ tự cột màn danh sách; chỉ đổi trình bày, KHÔNG đổi logic
          parse — backend map theo tên cột/alias, không theo vị trí) ══════════ */}
      {importPreview && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '24px',
            // ISS-PILOT-29 "Phát hiện phụ" (2026-08-12): bảng preview 8 cột (thêm SĐT)
            // rộng tự nhiên ~934px (whiteSpace:nowrap theo cột) > khung overflowX cũ
            // 912px (960 - 2*24 padding) → tràn ngang 22px, cột "Kết quả" bị cắt trước
            // khi cuộn. Nới maxWidth 960→1000px cho khung overflowX đủ chỗ (952px),
            // không đụng minWidth:720px trên <table> (không phải nguyên nhân — bảng đã
            // rộng hơn 720 do nội dung nowrap, không phải do minWidth ép).
            width: '92%', maxWidth: '1000px', maxHeight: '85vh', overflowY: 'auto',
          }} role="dialog" aria-modal="true" aria-labelledby="previewTitle">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <h2 id="previewTitle" style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
                Xem trước nhập — {importPreview.total} dòng ({importPreview.valid} hợp lệ · {importPreview.errors} lỗi)
              </h2>
              {/* D6: modal có [✕] góc trên */}
              <button
                onClick={() => { setImportPreview(null); setImportFile(null); }}
                aria-label="Đóng"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#6b7280', padding: '2px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', minWidth: '720px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    {/* 🔄 (2026-08-25, @tester verify screenshot): "Mã nhân
                        viên"/"Ngày vào làm" chèn TRƯỚC "Nhãn", khớp vị trí
                        khối [10d] trong modal (Mã nhân viên*→Ngày vào
                        làm*→Chức danh). SĐT vẫn đứng cạnh Họ tên (ISS-PILOT-29
                        — mô phỏng cách bảng danh sách chính gộp SĐT dưới Họ
                        tên CÙNG 1 cột; đây là quy ước hiển thị RIÊNG của bảng
                        Xem trước, KHÔNG phải thứ tự cột tệp — xem comment tại
                        dòng gợi ý "Cột yêu cầu:" phía trên cho thứ tự tệp thật). */}
                    {['Dòng', 'Họ tên', 'SĐT', 'Email', 'Mã vai trò', 'Mã đơn vị', 'Mã NV', 'Ngày vào làm', 'Nhãn', 'Kết quả'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px', color: '#6b7280', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importPreview.rows.map((r) => (
                    <tr key={r.row} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.row}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.full_name}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.phone}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.email}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.role_code}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.unit_code}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.employee_code || '—'}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.hire_date || '—'}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.label || '(trống→gợi ý)'}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap', fontWeight: 600, color: !r.ok ? '#dc2626' : r.action === 'review' ? '#b45309' : '#15803d' }}>
                        {r.ok ? (r.action === 'update' ? '✔ Cập nhật' : r.action === 'review' ? `⚠ Cập nhật — ${r.reason}` : r.action === 'skip' ? `— Bỏ qua: ${r.reason}` : '✔ Thêm') : `✖ ${r.reason}`}
                        {r.role_change && (
                          <div style={{ fontSize: '12px', fontWeight: 500, color: r.action === 'review' ? '#b45309' : '#374151' }}>
                            Vai trò: {r.role_change}
                          </div>
                        )}
                        {r.username && (
                          <div style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }}>Tên đăng nhập: {r.username}</div>
                        )}
                        {/* Track-1b BS-007-R12: cờ ⚠ trùng ngườu (KHÔNG chặn) */}
                        {r.ok && r.warning && (
                          <div style={{ fontSize: '12px', fontWeight: 500, color: '#b45309', whiteSpace: 'normal' }}>
                            ⚠ {r.warning}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ margin: '12px 0', fontSize: '14px', color: '#374151' }}>
              {importPreview.errors > 0
                ? `⚠ ${importPreview.errors} dòng lỗi (dòng ${importPreview.error_rows.join(', ')}) sẽ bị bỏ qua; chỉ nhập ${importPreview.valid} dòng hợp lệ.`
                : `${importPreview.valid} dòng hợp lệ.`}
            </div>
            {/* #1270 review r2: dòng "review" (đổi vai trò quản trị) chỉ được ghi khi TA
                xác nhận tường minh — BE từ chối nếu thiếu ?confirm_review=true. */}
            {importPreview.rows.some((r) => r.action === 'review') && (
              <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '14px', color: '#b45309', margin: '8px 0' }}>
                <input type="checkbox" checked={confirmReview} onChange={(e) => setConfirmReview(e.target.checked)} />
                Tôi đã xem kỹ các thay đổi vai trò quản trị (⚠) và xác nhận áp dụng
              </label>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={() => { setImportPreview(null); setImportFile(null); }}
                style={{ padding: '8px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
              >
                Hủy
              </button>
              <button
                onClick={handleImportCommit}
                disabled={committing || importPreview.valid === 0}
                style={{
                  padding: '8px 20px', background: committing || importPreview.valid === 0 ? '#9ca3af' : PRIMARY,
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: committing || importPreview.valid === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '500',
                }}
              >
                {committing ? 'Đang nhập…' : `Nhập ${importPreview.valid} dòng hợp lệ`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast kết quả nhập/xuất + BR-EMP-003 SAVED-DUAL (tạo/pairing hồ sơ NV) */}
      {(importToast || exportToast || savedToast) && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          background: '#1f2937', color: 'white', padding: '10px 20px',
          borderRadius: '8px', fontSize: '14px', zIndex: 1100,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {importToast || exportToast || savedToast}
        </div>
      )}
    </div>
  );
};

// Style helpers
const menuItemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 14px',
  background: 'white',
  border: 'none',
  cursor: 'pointer',
  fontSize: '13px',
  color: '#374151',
  whiteSpace: 'nowrap',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: '12px',
  fontWeight: '600',
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '14px',
  color: '#374151',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const paginationBtn = (disabled: boolean): React.CSSProperties => ({
  padding: '6px 14px',
  background: disabled ? '#f3f4f6' : 'white',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '14px',
  color: disabled ? '#9ca3af' : '#374151',
});

export default TenantUserManagement;
